import type { EarningsBreakdownRow } from './earnings-buckets';
import { DEFAULT_EMBED_HOSTS } from './player/provider';
import { PAYOUT_DEDUCTIONS_DEFAULT, type PayoutDeductionsConfig } from './payout-deductions';
import {
  ECOM_PAYMENT_METHODS_DEFAULT,
  type EcomPaymentMethodsConfig,
} from './ecom-payment-methods';
import { POS_PAYMENT_METHODS_DEFAULT, type PosPaymentMethodsConfig } from './pos-payment-methods';
import { POS_CUSTOM_FEES_DEFAULT, type PosCustomFeesConfig } from './pos-custom-fees';
import {
  MERCHANT_REQUIREMENTS_DEFAULT,
  type MerchantPayoutSchedule,
  type MerchantRequirementDef,
} from './merchant';
import { STOREFRONT_DEFAULT, resolveStorefront, type StorefrontConfig } from './storefront-content';
import type { AssistantPersonaOverride } from './assistant/personas';
import type { CoachingPlanFeatures } from './coaching/features';
import type { CoachingCreditCosts } from './coaching/credits';

/**
 * Cash-out availability window. When `enabled`, a member may only submit a
 * payout request on a day in `daysOfWeek` (0=Sun..6=Sat) and at a local hour in
 * `[openHour, closeHour)` (24h, tenant timezone). Disabled = always open.
 */
export type CashoutSchedule = {
  enabled: boolean;
  daysOfWeek: number[];
  openHour: number;
  closeHour: number;
};

/**
 * Is the cash-out window open at `nowLocalHour`/`nowLocalDay` (already resolved
 * to the tenant timezone)? Disabled schedules are always open. Pure so the
 * server gate and the member SPA can share one source of truth.
 */
export function isCashoutOpen(
  schedule: CashoutSchedule | null | undefined,
  nowLocalDay: number,
  nowLocalHour: number,
): boolean {
  if (!schedule || !schedule.enabled) return true;
  const days = Array.isArray(schedule.daysOfWeek) ? schedule.daysOfWeek : [];
  if (days.length > 0 && !days.includes(nowLocalDay)) return false;
  return nowLocalHour >= schedule.openHour && nowLocalHour < schedule.closeHour;
}

export const CASHOUT_SCHEDULE_DEFAULT: CashoutSchedule = {
  enabled: false,
  daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
  openHour: 0,
  closeHour: 24,
};

/**
 * Min/max bounds on cash-out requests, enforced in `POST /payouts/requests`.
 * `perSlot*` bound a SINGLE request's amount. `perMember*` bound the member
 * across ALL their slots: `perMemberMax` caps the sum of the member's
 * outstanding (requested/queued/processing) requests + the new one. `null` =
 * no bound. The effective single-request minimum is `max(perSlotMin,
 * perMemberMin)`.
 */
export type CashoutLimits = {
  perSlotMin: number | null;
  perSlotMax: number | null;
  perMemberMin: number | null;
  perMemberMax: number | null;
};

export const CASHOUT_LIMITS_DEFAULT: CashoutLimits = {
  perSlotMin: null,
  perSlotMax: null,
  perMemberMin: null,
  perMemberMax: null,
};

/**
 * Tenant-level constants for the payout batch disbursement file (the sheet
 * finance uploads to the bank). The per-member columns come off the request;
 * these are the ones that are the same on every row.
 *
 * `sender_mobile` / `sender_address` / `purpose` map 1:1 to the remittance-only
 * columns in the bank's template — blank for straight bank transfers, required
 * by some over-the-counter remittance processors.
 */
export type PayoutExportConfig = {
  /** Prefix on the generated transaction reference (`PE20260805000001`). */
  txn_prefix: string;
  /** Description column. Blank falls back to the payout method's label. */
  description: string;
  sender_mobile: string;
  sender_address: string;
  purpose: string;
};

export const PAYOUT_EXPORT_DEFAULT: PayoutExportConfig = {
  txn_prefix: 'PE',
  description: '',
  sender_mobile: '',
  sender_address: '',
  purpose: '',
};

/**
 * An admin-defined extra field collected on member signup, beyond the built-in
 * name/email/phone/etc. `type='text'` is a free-text input; `type='file'` is a
 * scanned upload (the signup form pre-uploads to a temp key and the value is
 * stored as `{ r2Key, filename }` on `users.registration_data[id]`). `id` is a
 * stable slug used as the storage key; `order` controls display order.
 */
export type RegistrationFieldType = 'text' | 'file';
export type RegistrationField = {
  id: string;
  label: string;
  type: RegistrationFieldType;
  required: boolean;
  order: number;
};

/**
 * Which fields a MEMBER may edit on their own Profile tab. Each flag gates both
 * the input (member SPA disables it) AND the server (`PATCH /me` rejects a
 * locked field). `name` governs first/middle/last together. Email, username,
 * and address are intentionally NOT here: email & username are permanently
 * read-only identity, and address has its own dedicated Address tab / checkout
 * editor (the Profile tab only shows it read-only). All default to editable so
 * an un-configured tenant keeps today's behavior.
 */
export type MemberEditableFields = {
  name: boolean;
  phone: boolean;
  profilePicture: boolean;
};
export const MEMBER_EDITABLE_FIELDS_DEFAULT: MemberEditableFields = {
  name: true,
  phone: true,
  profilePicture: true,
};

/**
 * Which detail fields show on each box (slot node) of the BINARY genealogy tree,
 * in both the member Genealogy page and the admin slot-detail Genealogy tab (the
 * tree component is shared). A display control only — the data is always fetched
 * and hidden client-side (like all other display flags), not a hard security
 * boundary. `fullName`/`slotCode` default ON (today's node card always shows the
 * owner + slot code); the binary points + sponsor fields default OFF (opt-in,
 * matching {@link show_sponsor_on_dashboard}). Rank / package / currency are not
 * configurable here and always show.
 */
export type BinaryGenealogyNodeFields = {
  fullName: boolean;
  slotCode: boolean;
  /** The membership/kit package amount (e.g. ₱23,400.00) + its currency unit. */
  packageAmount: boolean;
  leftPoints: boolean;
  rightPoints: boolean;
  sponsorName: boolean;
  sponsorSlotCode: boolean;
  /** The slot's current CASH wallet balance (slot_wallets.available). */
  walletBalance: boolean;
};
export const BINARY_GENEALOGY_NODE_FIELDS_DEFAULT: BinaryGenealogyNodeFields = {
  fullName: true,
  slotCode: true,
  // Off by default: the package amount is the same for everyone in a tier
  // (the kit price), so it reads as noise on the tree. Re-enable per tenant.
  packageAmount: false,
  leftPoints: false,
  rightPoints: false,
  sponsorName: false,
  sponsorSlotCode: false,
  // Off by default: a downline's wallet balance is sensitive — the tree is
  // visible to members, so exposing it is an explicit per-tenant decision.
  walletBalance: false,
};

/**
 * Which fields a MERCHANT may edit on their own Profile page. Same enforcement
 * posture as {@link MemberEditableFields} (input disabled + `PATCH /merchant/me`
 * rejects a locked field). `ownerName` edits the linked account's display name
 * (`users.full_name`). The account login email & username are permanently
 * read-only and not represented here. `pickupAddress` covers the pickup line /
 * city / region (the shipping origin) as one group. All default to editable.
 */
export type MerchantEditableFields = {
  businessName: boolean;
  ownerName: boolean;
  contactNumber: boolean;
  contactEmail: boolean;
  pickupAddress: boolean;
};
export const MERCHANT_EDITABLE_FIELDS_DEFAULT: MerchantEditableFields = {
  businessName: true,
  ownerName: true,
  contactNumber: true,
  contactEmail: true,
  pickupAddress: true,
};

/**
 * Admin-defined KYC document requirement. The member's KYC tab renders one
 * upload slot per entry (in order); each uploaded file's `key` is stored as the
 * `kind` on `kyc_submissions.documents[]`. Admins manage the list from the
 * Cash-out settings. All entries are image uploads.
 */
export type KycRequirementDef = {
  /** Stable slug stored on `kyc_submissions.documents[].kind`. */
  key: string;
  label: string;
  /** Help text shown under the upload slot. */
  description?: string;
  /** When false, the member may submit without it. */
  required: boolean;
};

/** Starter set mirroring the previously hardcoded slots; admins can edit it. */
export const KYC_REQUIREMENTS_DEFAULT: KycRequirementDef[] = [
  { key: 'gov_id_front', label: 'Government ID — front', required: true },
  { key: 'gov_id_back', label: 'Government ID — back', required: true },
  { key: 'selfie', label: 'Selfie holding your ID', required: false },
];

/**
 * One named group of plans that share a combined daily cash-income cap.
 * `id` is a stable UUID (the per-tier override and UI key on it, never the
 * mutable `label`). `limit` is the shared daily ceiling; `<= 0` = no cap.
 */
export type DailyCapGroup = {
  id: string;
  label: string;
  limit: number;
  plans: string[];
};

/**
 * One wallet top-up bonus bracket. When an approved top-up's amount falls in
 * [minAmount, maxAmount] (both INCLUSIVE; maxAmount null = open-ended top
 * bracket), the member receives a bonus credit. `bonusKind` decides BOTH the
 * math (percentage of the top-up amount vs a fixed amount) AND the destination
 * wallet (cash vs gc) — independent of the kind being topped up, so "top up
 * cash, get a GC bonus" is allowed. Money fields are decimal strings.
 *
 * Brackets are matched in array order: the FIRST match wins (overlaps are NOT
 * summed). Admins arrange the order in Settings.
 */
export type WalletTopupBracket = {
  id: string;
  minAmount: string;
  maxAmount: string | null;
  bonusKind: 'pct_wallet' | 'pct_gc' | 'fixed_wallet' | 'fixed_gc';
  /** Percent (e.g. "2" = 2%) for pct_*, or a fixed money amount for fixed_*. */
  value: string;
  /**
   * A gift check program (`voucher_programs.id`, earned by `topup`) of which one
   * gift check is issued when an approved top-up falls in this bracket. Absent
   * or null = none. A bracket may carry only this, with a `value` of 0.
   */
  voucherProgramId?: string | null;
};

/**
 * Give every member who signs up a FREE slot at once (cashier phase 5). A pure
 * loyalty tenant sells no kits, and without a slot a member has no wallet to top
 * up or pay from. `membership_id` must be a free-slot eligible tier.
 */
export type SignupFreeSlotConfig = {
  enabled: boolean;
  membership_id: string | null;
};

export const SIGNUP_FREE_SLOT_DEFAULT: SignupFreeSlotConfig = {
  enabled: false,
  membership_id: null,
};

/** Where a member's birth date is asked for (cashier phase 5). */
export const BIRTH_DATE_FIELD_MODES = ['hidden', 'optional', 'required'] as const;
export type BirthDateFieldMode = (typeof BIRTH_DATE_FIELD_MODES)[number];

/** How a top-up bonus is handled when the top-up is paying down a CD debt. */
export const CD_TOPUP_BONUS_TREATMENTS = [
  'principal_only',
  'include_cash_bonus',
  'no_bonus',
] as const;
export type CdTopupBonusTreatment = (typeof CD_TOPUP_BONUS_TREATMENTS)[number];

/** Which slot's price a member holding several slots pays. */
export const PRICING_SLOT_SOURCES = ['active_slot', 'best_across_slots'] as const;
export type PricingSlotSource = (typeof PRICING_SLOT_SOURCES)[number];

/**
 * Strongly-typed view of `tenant_config.features` (jsonb in DB). New keys land
 * here as they're added across phases; existing routes/services read them via
 * `getFeatures(db)` so a missing key gives the documented default, never
 * `undefined`.
 *
 * Adding a new key:
 *   1. Add the field + default below.
 *   2. Read it via `getFeatures(db).<key>` — never index the raw jsonb.
 *   3. If admins should be able to toggle it, expose it through
 *      `GET/PATCH /admin/tenant/features` (super_admin gated).
 */
export type TenantFeatures = {
  /**
   * If true, kit_codes issued from then on may be redeemed by any user (gift
   * card behavior). If false, only the original buyer may redeem. Existing
   * codes keep their issuance-time semantics — flipping this never
   * retroactively locks already-issued codes.
   */
  kit_codes_transferable: boolean;

  /**
   * Number of days a newly-issued kit code stays valid. `null` = never
   * expires. Like transferability, this is a snapshot taken at issuance.
   */
  kit_codes_expire_days: number | null;

  /**
   * If true, a member may transfer an unredeemed code from their own Code Vault
   * to a member in their downline (identified by username + email). The code
   * leaves the sender's vault and appears in the recipient's. Off by default.
   */
  kit_codes_downline_transfer: boolean;

  /**
   * F5: auto-create (auto-redeem) the slot when a Membership Kit is purchased,
   * per sales channel. When on for a channel, each freshly-minted activation code
   * from that channel's order is immediately redeemed for the buyer — sponsor +
   * binary placement resolved from registration (as a no-anchor redeem),
   * activation terms auto-accepted, no manual Code+PIN Vault step. Upgrade-only
   * codes are left for a manual upgrade; extra codes past the member's slot cap
   * stay redeemable in the Vault. All three default off.
   *
   * `virtual` is a THIRD dimension, not a channel: it fires for an order whose
   * lines are all virtual (`metadata.virtual_only`), whichever channel sold it.
   * A virtual kit has no physical fulfilment and already auto-completes on
   * payment, so making the buyer hunt a Code+PIN in the Vault is friction that
   * only makes sense for a kit somebody has to hand over.
   *
   * ADDITIVE — `channelFlag || (isVirtualOnly && virtual)`. Turning `virtual` on
   * can only ever enable auto-create; it can never switch it off for a channel
   * that already has it, which would be a surprising way to lose behaviour.
   */
  kit_auto_create_slot: { ecom: boolean; cashier: boolean; virtual: boolean };

  /**
   * If true, members must have TOTP enrolled. `/auth/login` returns
   * `totp_setup_required` for members who don't yet have it. Admin + cashier
   * 2FA is always required regardless of this flag.
   */
  member_2fa_required: boolean;

  /**
   * What "archive this account" DOES.
   *
   *   `deactivate` (default) — `users.status` becomes `'archived'` and the
   *      member's refresh tokens are destroyed, so they are signed out
   *      everywhere and cannot sign in. NOTHING else changes: the profile,
   *      KYC, payout accounts, wishlist and notifications all survive, and the
   *      member (or an admin) can bring the account back. This is what closing
   *      an account means for almost every tenant.
   *   `erase` — additionally scrubs every identity column, destroys the
   *      credential, deletes the cascade-safe personal tables and queues the
   *      member's stored files for deletion. IRREVERSIBLE, and only for a
   *      tenant with an actual erasure duty.
   *
   * Slots, orders, the ledger, certificates and the member's place in the
   * genealogy tree survive in BOTH modes: they are the company's records, and
   * `users.id` carries ~150 inbound foreign keys.
   */
  account_archive_mode: 'deactivate' | 'erase';

  /**
   * Suspend a member's account (`users.status='suspended'`, login refused with
   * `account_blocked_inactivity`) once they go `inactivity_block_days` with NO
   * activity. Activity is any of: a paid order they bought, a slot of theirs
   * activating, or a new slot activating under one of their slots (a direct).
   * Swept by the worker via `POST /internal/members/inactivity-sweep`. Staff
   * and company-slot owners are never touched. Default OFF.
   *
   * Suspension does NOT stop commissions: comp reads `slots.status`, exactly
   * as with a manual block.
   */
  inactivity_block_enabled: boolean;
  /** Idle days before the block. Default 365. */
  inactivity_block_days: number;
  /**
   * SERVER-OWNED grace anchor (ISO timestamp), stamped whenever
   * `inactivity_block_enabled` goes off → on. No member's clock starts before
   * it, so switching the rule on never blocks anyone that same day. Not in the
   * PATCH schema: the admin cannot write it directly.
   */
  inactivity_block_since: string | null;

  /**
   * Override for what triggers `order_completed` events on ecom orders.
   *   `payment_approved` (default) — fires the moment manual-payment is
   *      approved. Commissions land immediately.
   *   `order_completed` — fires only when fulfillment marks the order
   *      `completed`. Pushes commissions later but matches physical delivery.
   * POS orders always fire on checkout (in-person hand-off).
   */
  ecom_event_on: 'payment_approved' | 'order_completed';

  /**
   * When the Kit Code+PIN becomes visible to the buyer in their Vault for an
   * ecom kit order. Earlier reveal = faster member activation, but redeeming
   * the code marks that line item non-refundable even if the kit hasn't
   * physically arrived yet.
   *
   *   `payment_approved` — codes unlock the moment payment is approved (`paid`)
   *   `shipped`          — codes unlock when admin marks the order `shipping`
   *   `delivered`        — codes unlock when admin marks the order `delivered`
   *   `completed`        — codes unlock only after the member confirms receipt
   *                        (default; preserves the original behavior)
   * Wallet-instant ecom orders (pickup / address-less, or when
   * `wallet_orders_charge_shipping` is off) always mint codes immediately at
   * checkout, regardless of this setting. A wallet order that walks the ladder
   * (address + `wallet_orders_charge_shipping` on) instead honours this setting
   * like any other ecom order.
   */
  kit_code_reveal_on: 'payment_approved' | 'shipped' | 'delivered' | 'completed';

  /**
   * Number of days an order may sit awaiting the member's receipt confirmation
   * (`delivered` for shipping, `to_pick` for pickup) before the worker
   * auto-completes it. `null` or `0` disables the sweep (orders wait forever for
   * a manual "Mark received"). Default 7. Drives the periodic auto-complete job
   * in `apps/worker`; admin-adjustable in Tenant Settings.
   */
  order_auto_complete_days: number | null;

  /**
   * When true (default), an ecom order whose every line is a VIRTUAL product
   * completes the moment payment lands — there is nothing to pick, pack, ship,
   * or hand over, so parking it at `paid` only makes an admin click twice.
   *
   * Deliberately narrow. It fires ONLY for an order stamped
   * `metadata.virtual_only` at creation (see `createOrderInTx`), so:
   *  - a MIXED cart (virtual + physical) is one order and walks the full ladder;
   *  - a branch-PICKUP order is untouched even though it also has no ship-to
   *    address — the marker is what an address-based check would get wrong;
   *  - a COD-Manual order never reaches `paid` at all, so nobody is credited
   *    for goods that haven't been paid for.
   * POS is untouched (it already completes at checkout, or parks at
   * `to_release` under `branches.completion_mode = 'manual'`).
   *
   * Two knock-on effects, both intended: under `ecom_event_on='order_completed'`
   * commissions on an all-virtual order now land at payment instead of waiting
   * for an admin, and under `kit_code_reveal_on='completed'` a virtual kit's
   * Code+PIN unlocks at payment (which also makes that line non-refundable
   * earlier). Turn this off to keep the manual review step.
   */
  virtual_orders_auto_complete: boolean;

  /**
   * When true, a MIXED ecom cart (virtual + physical lines) is split by the
   * server into TWO orders sharing one `checkout_group_id`: a physical order
   * that walks the normal shipping ladder, and a virtual order that carries no
   * address, no courier and no fee, is stamped `metadata.virtual_only`, and so
   * completes on payment via `virtual_orders_auto_complete`. Default OFF — the
   * cart stays one order exactly as before.
   *
   * The point is delivery independence: today a buyer who adds a course and a
   * t-shirt to one cart waits for the t-shirt to ship before the course is
   * released. One cart, one payment, two fulfilment tracks.
   *
   * Deliberate boundaries:
   *  - An all-virtual or all-physical cart is NEVER split (there is no second
   *    half to make), so nothing changes for a single-kind cart.
   *  - COD-Manual is excluded: cash is collected once on hand-over, and a
   *    virtual half has nothing to hand over — it would strand on the ladder
   *    (`virtual-auto-complete.ts` excludes COD for the same reason).
   *  - The coupon rides EXACTLY ONE half (`chooseCouponHalf`): a redemption
   *    burns a use per order, so passing the code to both would fail the second
   *    with `coupon_exhausted`.
   *  - Only the physical half receives shipping args; the buyer pays once via
   *    the existing group-payment machinery (`submitManualPaymentGroup`, group
   *    gateway initiate, the member `OrderGroupCard`).
   */
  virtual_orders_split_checkout: boolean;

  /**
   * Phase 13.5: list of `plan_code`s the tenant doesn't use. Disabled plans
   * are skipped by the worker before `runEvent()` and hidden from the admin
   * Distribute / Distributions / ComPlan editor UIs. Plans not listed here
   * are active. Removing a code from this list re-enables the plan
   * immediately on the next event.
   *
   * Example: a tenant who doesn't run a vortex sign-up bonus can set
   * `["sign_up_vortex", "mentor_bonus"]` to silence them without deleting
   * their configs.
   */
  disabled_plans: string[];

  /**
   * Display order for the member "Earnings Breakdown" panel: an ordered list of
   * ledger `source_plan` codes. The panel groups the member's lifetime credits
   * by source_plan; codes listed here render first in this order, any earning
   * plan not listed renders after (sorted by amount). Disabled plans never show
   * regardless. Admin-arranged in Settings; empty = amount-sorted default.
   *
   * @deprecated Superseded by `earnings_breakdown_rows` (order + per-row
   * visibility mode). Kept as a read fallback only: `resolveEarningsBreakdownRows`
   * consults this when the new key is empty, so legacy tenants keep their
   * arrangement with zero data rewrite. The Settings editor writes the new key.
   */
  earnings_breakdown_order: string[];

  /**
   * Member "Earnings Breakdown" row config: array order = display order, one
   * entry per bucket key — a ledger `source_plan` code, or a sub-bucket key
   * from `EARNINGS_SUB_BUCKETS` (e.g. the Monthly Direct mentor rider split).
   * Per-row `mode`: `always` shows the row even at zero earnings,
   * `when_earned` (the default) only when it has credits, `hidden` suppresses
   * an active plan's row. Labels are NOT stored here — rows read/write the
   * shared `ledger_source_plan_labels` / `ledger_labels` maps, so a rename
   * follows everywhere (passbook, notifications, admin views). Empty = fall
   * back to the deprecated `earnings_breakdown_order`, then amount-sorted.
   */
  earnings_breakdown_rows: EarningsBreakdownRow[];

  /**
   * Member wallet top-ups: a member submits an amount + proof-of-payment image,
   * an admin approves it, and the slot wallet is credited. OFF by default.
   */
  wallet_topup_enabled: boolean;
  /** Bonus brackets applied on top-up approval (first matching bracket wins).
   *  Members pay via the same MANUAL `ecom_payment_methods` they use at checkout
   *  (GCash, bank transfer, …) — there is no separate top-up method list. */
  wallet_topup_brackets: WalletTopupBracket[];

  /**
   * Plans paid EXCLUSIVELY via an ad-hoc distribution run (admin-chosen date
   * window), never at the monthly `payout_period_closed`. A plan listed here is
   * skipped by the worker on `payout_period_closed` ONLY — its realtime
   * accumulation events (e.g. Binary leg banking on `slot_placed`/
   * `order_completed`) still run. Without this, a plan that handles BOTH the
   * period close AND a distribution run (e.g. `monthly_direct_bonus`, `binary`)
   * double-pays an overlapping window: the two paths use different `event_id`s,
   * so the ledger's per-event idempotency never dedupes them. Default empty
   * (no change in behavior); a tenant that distributes these ad-hoc should add
   * them here so the period close doesn't also pay them.
   */
  distribution_run_only_plans: string[];

  /**
   * Plan codes for which COMPANY slots are "auto-maintained": exempt from that
   * plan's per-period personal-PV (maintenance) gate. A company slot listed for
   * a plan always passes that plan's PPV/maintenance requirement regardless of
   * its actual repurchase; a plan NOT listed treats company slots like any other
   * slot. Only plans that HAVE a maintenance gate are meaningful here
   * (`stairstep`, `unilevel`, `maintenance_cashback`, `monthly_direct_bonus`).
   * Defaults to `['stairstep','unilevel']` — the two plans that were previously
   * hard-coded to always exempt company slots — so behavior is unchanged until
   * an admin edits the list.
   */
  company_auto_maintained_plans: string[];

  /**
   * Phase A (spec conformance): GLOBAL per-plan daily cash-income cap, keyed by
   * `plan_code`. The framework clamps a plan's daily cash credits per receiver
   * slot to this amount. A per-membership-tier override
   * (`memberships.config.daily_caps[plan_code]`) takes precedence when present;
   * the effective cap is `tierOverride ?? global ?? unlimited`. A missing key
   * or `0` means NO LIMIT. Binary is exempt (it self-caps via
   * `binary_daily_state` / `daily_income_limit`).
   *
   * Example: `{ "direct": 5000, "indirect": 2000 }`.
   */
  daily_caps: Record<string, number>;

  /**
   * If true, slot creation auto-places in the binary tree via spillover under
   * the sponsor when no explicit placement payload is given. If false
   * (default), slots are created unplaced and must be placed via
   * `POST /slots/:id/place` — the owner picks an exact L/R seat. Explicit
   * placement requests in the create body always honor the seat regardless
   * of this flag.
   */
  auto_place_slots: boolean;

  /**
   * When `true` (opt-in), the member redeem/activation form shows the "Place in
   * my binary tree now (otherwise place it later)" checkbox + anchor/side picker
   * (only relevant once the member already has ≥1 active slot). When `false`
   * (default), the checkbox is hidden and placement falls through to the server
   * default (no explicit anchor → `auto_place_slots` decides auto-spillover vs
   * unplaced). UI-only — server placement logic is unchanged.
   */
  manual_placement_on_redeem: boolean;

  /**
   * When `true` (default), the genealogy tree shows the "Activate New Slot" /
   * "Add New Slot" action card. When `false`, that entry point is hidden — the
   * Code Vault redeem flow still works. UI gate only.
   */
  genealogy_activate_slot: boolean;

  /**
   * When `true` (default), a `slot_type='free'` slot EARNS normally — it
   * receives commissions from its downlines like any paid slot. When `false`,
   * `runEvent` drops any proposed ledger credit whose recipient is a free slot
   * (the historical BF2 behavior). Either way, CREATING a free slot still fires
   * no upline comp (redeem suppresses `slot_activated`/`slot_placed` for free
   * slots) — this flag only governs whether a free slot can RECEIVE earnings.
   */
  free_slots_earn: boolean;

  /**
   * Campaign kit scoping. Master switch for the whole feature (default `true`).
   *
   * When `true`, a still-slotless lead is SCOPED to the activation kits tagged
   * to the campaign page they signed up through
   * (`replicated_pages.kit_product_ids`): the storefront, checkout and dashboard
   * hide OTHER kits while keeping every regular (non-kit) product available. A
   * campaign that tags nothing is unrestricted, so the flag is a no-op until at
   * least one campaign is tagged. When `false`, no kit scoping is applied at all.
   *
   * Scope derives live from `users.registered_via_page_id` (no per-package URL,
   * no extra column) and lifts automatically once the lead activates a slot.
   * Resolved by `resolveCampaignKitScope`, enforced in `listStorefrontProducts`
   * and re-validated at checkout (`createOrderInTx`).
   *
   * Until 2026-08 this ALSO intersected a per-tier list on the sponsor's
   * membership. That half was retired with its admin UI: two sources could
   * intersect to empty, which failed closed and left a lead unable to buy the
   * kit their campaign was selling.
   */
  package_replicated_link_enabled: boolean;

  /**
   * Member dashboard "Total saved" card (default `false`). When `true`, the
   * dashboard shows the lifetime sum of every discount the member received
   * across their online-store (ecom) and cashier (POS) purchases: per-product
   * membership/rank/stockist price discounts (`orders.discount_total`),
   * coupons (`orders.coupon_total`), and POS manager discounts
   * (`orders.manager_discount_amount`). Display-only — no comp effect. Cash
   * orders only (non-cash carries no discounts); legacy-imported and
   * cancelled/refunded/never-paid orders excluded.
   */
  total_saved_counter_enabled: boolean;

  /**
   * CD/Half-CD: when `true` (default), an IN-DEBT cd/half_cd slot's own
   * purchases fire `order_completed` comp for its uplines like any slot. When
   * `false`, the worker skips the commission plans for orders bought by an
   * in-debt CD slot (or in-debt Half-CD while `half_cd_triggers_like_ps` is
   * off) but still runs the PV rollup + live rank-up — volume flows, money
   * doesn't. Skipped commissions (incl. binary leg points from that order) are
   * NOT re-fired at settlement.
   */
  cd_orders_pay_upline: boolean;

  /**
   * CD: when `true` (default), an in-debt `cd` slot counts toward its
   * sponsor's Stairstep leg requirements (`min_directs` / `min_legs`) like any
   * active slot. When `false`, it only counts after its debt is settled (the
   * slot flips to `paid`). GPV contribution is untouched either way.
   */
  cd_stairstep_count_immediately: boolean;

  /** Same as `cd_stairstep_count_immediately`, but for in-debt `half_cd` slots. */
  half_cd_stairstep_count_immediately: boolean;

  /**
   * CD: when `true` (default), an in-debt `cd`/`half_cd` direct counts toward
   * its mentor's **Binary Mentor's Bonus** per-level `min-directs` requirement
   * like any active direct. When `false`, in-debt CD/Half-CD directs are
   * ignored for that requirement (they count again once settled → `paid`).
   * Mirrors the Stairstep leg gating; applies to the binary plan's mentor rider
   * (legacy/extreme + teslab variants), not the standalone per-tier mentor bonus.
   */
  cd_binary_mentor_count_directs: boolean;

  /**
   * CD: when `true` (default), an in-debt `cd`/`half_cd` direct counts toward
   * the **Extreme Binary preferred-placement (side) direct requirement** — the
   * gate that unlocks changing a slot's L/R spillover side. When `false`,
   * in-debt CD/Half-CD directs don't count until settled (`paid`).
   */
  cd_binary_placement_count_directs: boolean;

  /**
   * Half-CD: when `true`, redeeming a half_cd kit code fires entry comp
   * (`slot_activated`/`slot_placed`) immediately like a paid slot — uplines
   * are paid upfront and settlement then fires NOTHING (it only stops the
   * garnish). When `false` (default), half_cd defers entry comp to settlement
   * exactly like `cd`. Snapshotted onto `slot_debts.entry_comp_fired` at
   * redemption, so flipping the toggle later never double-fires.
   */
  half_cd_triggers_like_ps: boolean;

  /**
   * Half-CD: plan codes (`sourcePlan`, e.g. 'binary', 'unilevel') whose CASH
   * earnings are garnished into an in-debt half_cd slot's debt. Earnings from
   * plans NOT in this list credit the wallet normally. Empty (default) = a
   * half_cd slot's debt is only payable via admin adjustment. `cd` slots
   * ignore this list — every plan garnishes them.
   */
  half_cd_debt_plans: string[];

  /**
   * CD: when `true` (default), a cash payout request from a slot whose
   * `slot_type` is `'cd'` with an OPEN debt is rejected (`slot_in_debt`) — a
   * `cd` slot's cash must garnish into the debt, not cash out. `half_cd` slots
   * are ALWAYS allowed to cash out (their debt only garnishes `half_cd_debt_plans`
   * earnings, so other cash is legitimately withdrawable). A settled slot is
   * already `slot_type='paid'` and is never blocked. When `false`, in-debt `cd`
   * slots may cash out any materialized balance.
   */
  cd_block_payout_in_debt: boolean;

  /**
   * CD/Half-CD: when `true` (default), an approved member top-up to an in-debt
   * cd/half_cd slot is GARNISHED into that slot's debt (the deposited cash pays
   * the debt first, exactly like the slot's earnings do; any amount above the
   * remaining debt lands in the wallet). This is the member's self-serve payoff
   * — it garnishes BOTH cd and half_cd regardless of `half_cd_debt_plans` (that
   * list governs which EARNINGS garnish, not the member's own payment). When
   * `false`, top-ups always credit the wallet, even for an in-debt slot.
   */
  cd_topup_settles_debt: boolean;

  /**
   * How a top-up's BONUS (from `wallet_topup_brackets`) is treated when the
   * top-up is paying down a CD debt (only relevant while `cd_topup_settles_debt`
   * is on and the slot is in debt):
   *  - `principal_only` (default): only the deposited cash pays the debt; any
   *    cash/GC bonus credits the wallet as usual.
   *  - `include_cash_bonus`: the deposited cash AND any CASH bonus garnish into
   *    the debt (GC bonus can't — the debt is cash-only).
   *  - `no_bonus`: no bonus is granted at all on a debt-paying top-up.
   */
  cd_topup_bonus_treatment: CdTopupBonusTreatment;

  /**
   * Maximum number of slots a single member may own. `null` (default) =
   * unlimited. Enforced at slot creation (`createSlot`); the member area shows
   * `current_slot_count / max_slots_per_member`.
   */
  max_slots_per_member: number | null;

  /**
   * Slot-code series numbering. When `enabled`, a new slot's code keeps the
   * usual random base (e.g. "SK8N3PQRX7M") and gets a per-member ordinal suffix
   * ("-01", "-02", …) reflecting how many slots the member already has. Off by
   * default; only affects slots created after enabling (no retro-renumber).
   */
  slot_code_series: { enabled: boolean };

  /**
   * BF2: when true, a paid->paid slot upgrade (tier bump on an already-earning
   * slot) RE-FIRES activation comp (sign-up bonuses etc.) for the new tier.
   * When false (default), a paid->paid upgrade is a plain tier change with no
   * comp re-fire. (A free->paid upgrade ALWAYS fires activation regardless —
   * that slot is earning for the first time.) Some tenants want tier-up
   * bonuses, some don't, so it's configurable.
   */
  paid_upgrade_refires_bonuses: boolean;

  /**
   * When true (default), a slot's Stairstep rank is recomputed LIVE on slot
   * activation and product repurchase (the worker re-evaluates the slot's rank
   * from its current period PV right after the PV rollup) so the rank — and the
   * rank-based product pricing that follows it — updates immediately. When
   * false, rank only advances at period close (the legacy batch behavior).
   * Ranks are monotonic either way: a recompute can only RAISE a rank, never
   * lower it.
   */
  stairstep_live_rankup: boolean;

  /**
   * When true, reaching a new (higher) Stairstep rank pays a one-time bonus
   * from that rank's `reward_cash` / `reward_gc` (configured per rank in the
   * comp-plan editor). Paid once per (slot, rank) for the slot's lifetime.
   * Default false — most tenants don't run a rank-up bonus.
   */
  stairstep_rankup_bonus: boolean;

  /**
   * Public Stairstep rank showcase: the ranks (name, icon, description,
   * achiever count) and who holds each one, as masked names + photos. Gates
   * the public `GET /ranks*` endpoints, the Omni storefront `rankShowcase`
   * section and the member dashboard panel. Default false so a live box
   * shows nothing new on deploy; exposed on `GET /tenant/config` as
   * `rankShowcase`.
   */
  rank_showcase_enabled: boolean;

  /**
   * Deductions applied when a member requests a payout: withholding tax
   * (flat-rate-by-bracket), a service charge, and an admin fee — keyed per
   * payout method with a `default` fallback. The wallet is still debited the
   * GROSS amount; deductions determine the NET the member receives. All zero
   * (disabled) by default. See `payout-deductions.ts` for the math.
   */
  payout_deductions: PayoutDeductionsConfig;

  /**
   * When the cash-out window is open. `POST /payouts/requests` is rejected
   * outside it (and the member SPA disables the submit button + shows the next
   * open time). Evaluated in the tenant timezone (`tenant_config.timezone`).
   * `daysOfWeek` is 0=Sun..6=Sat; the window is `[openHour, closeHour)` in
   * 24h local time. Disabled by default (always open).
   */
  cashout_schedule: CashoutSchedule;

  /**
   * When true, a member must have `kyc_status = 'verified'` before
   * `POST /payouts/requests` is accepted (else `kyc_not_verified`). When false,
   * cash-out skips the KYC gate entirely. Defaults to TRUE to preserve the
   * historical hard requirement; admins toggle it from Tenant Settings.
   */
  cashout_require_kyc: boolean;

  /**
   * When true, `POST /payouts/requests` overwrites every payout-method field
   * tagged `role: 'account_holder'` with the account owner's `users.full_name`
   * (server-authoritative; the member can't type a different name). Guards the
   * "Person A accessed Person B's account and cashed out under A's name" case.
   * No-op if no method field is tagged `account_holder`. Default OFF (opt-in);
   * existing tenants keep the free-text name field.
   */
  lock_cashout_name_to_owner: boolean;

  /**
   * Autofill mode for cash-out fields tagged `role: 'email'` /
   * `role: 'contact_number'` (tagged per method in Fees & Tax):
   *   'off'     — (default) member types both, today's behavior.
   *   'prefill' — the SPA seeds the fields from the owner's profile
   *               (`users.email` / `users.phone`); member may edit.
   *   'locked'  — seeded AND read-only; the server overwrites the submitted
   *               payload with the profile values (authoritative, like
   *               `lock_cashout_name_to_owner`). A field whose profile value is
   *               EMPTY (phone is nullable) stays editable so the member is
   *               never bricked — the server only overwrites non-empty values.
   */
  cashout_contact_autofill: 'off' | 'prefill' | 'locked';

  /**
   * When true, a member must have a saved payout QR for the method they're
   * cashing out with before `POST /payouts/requests` is accepted (else
   * `payout_qr_required`). The QR is managed in the member's Settings → Payout
   * QR tab and is snapshotted onto the request at submit time. Default OFF
   * (opt-in) — the QR is a convenience for the disbursing clerk, not a
   * universal requirement, and turning it on blocks every member who hasn't
   * uploaded one yet.
   */
  payout_qr_required: boolean;

  /**
   * When true, a cash-out account number is UNIQUE across members: the first
   * member to cash out with a given (method, account number) claims it in the
   * `payout_accounts` registry, and any OTHER member submitting the same account
   * is rejected (`cashout_account_taken`). The same member may reuse their own
   * account across their slots. The account-number value is read from the field
   * tagged `role: 'account_number'` (fallback: legacy `account` key). Default OFF
   * (opt-in); existing tenants see no change.
   */
  unique_cashout_account_per_user: boolean;

  /**
   * When true, only members with `kyc_status = 'verified'` may pay with a
   * Cash-on-Delivery (COD/CODM) ecom payment method: the server omits COD from
   * `GET /storefront/payment-methods` for unverified members and `POST /orders`
   * rejects a COD order with `kyc_required_for_cod`. When false (default), COD
   * is unrestricted — this is opt-in, so existing tenants see no change.
   */
  cod_requires_kyc: boolean;

  /**
   * "Approve anyway" on proof of payment. Normally an admin must blind-type the
   * reference number off the proof image (`POST /payments/manual/:id/
   * verify-reference`) before Approve unlocks; without it the review throws
   * `reference_not_verified`. That gate has no escape hatch, so a legitimate
   * payment whose reference is illegible/cropped can never be approved.
   *
   * When true, a holder of `payment.approve` may approve WITHOUT verifying, by
   * passing `force: true` plus a mandatory free-text `forceReason`. The override
   * writes a second audit row (`manual_payment.force_approved`) carrying the
   * reason, mirroring `manual_payment.reference_mismatch_approved`.
   *
   * Deliberately NARROW — it overrides ONLY the unverified-reference gate. An
   * infected upload (`cannot_approve_infected_upload`) and a duplicate
   * admin-typed reference stay hard-blocked; neither is a legibility problem and
   * overriding them buys nothing. Default FALSE (opt-in).
   */
  manual_payment_force_approve: boolean;

  /**
   * Admin-defined KYC document requirements the member uploads for review (one
   * upload slot per entry). Empty falls back to the built-in starter set in the
   * member UI so KYC is never un-submittable. See {@link KycRequirementDef}.
   */
  kyc_requirements: KycRequirementDef[];

  /**
   * Min/max bounds on cash-out requests (per-slot single-request bounds +
   * per-member aggregate bounds). Enforced server-side; the member SPA reads
   * them to hint + pre-disable submit. All `null` (unbounded) by default.
   */
  cashout_limits: CashoutLimits;

  /**
   * When true (default), a member may have multiple outstanding cash-out
   * requests at once. When false, a new `POST /payouts/requests` is rejected
   * (`existing_pending_cashout_request`) while the member already has a request
   * in `requested`/`queued`/`processing` — the scope of "already has one" is
   * controlled by `cashout_multiple_scope`.
   */
  multiple_cashout_requests_allowed: boolean;

  /**
   * What counts as an existing pending request when
   * `multiple_cashout_requests_allowed` is false: `'member'` blocks if ANY of
   * the member's slots has a pending request; `'slot'` blocks only the same
   * slot (a different slot may still request). Defaults to `'member'`.
   */
  cashout_multiple_scope: 'member' | 'slot';

  /**
   * When true (default), a member may cancel their own not-yet-batched
   * (`requested`) cash-out request via `POST /payouts/requests/:id/cancel`.
   * When false, members cannot self-cancel (`cancellation_not_allowed`); only
   * an admin reject can reverse a request.
   */
  cashout_cancellation_allowed: boolean;

  /** Tenant constants stamped on every row of the payout disbursement file. */
  payout_export: PayoutExportConfig;

  /**
   * Admin-managed registry of ecom payment methods. Each entry pairs a
   * `currency` (cash | gc | coin) with a `settlement` ('wallet' for instant
   * slot-wallet debit, 'manual' for proof-of-payment upload + admin approval).
   * Members pick one at checkout; the route resolves wallet/manual via
   * `resolveEcomPaymentMethod`. Empty by default — admin populates it from
   * Tenant Settings. See `ecom-payment-methods.ts` for the shape.
   */
  ecom_payment_methods: EcomPaymentMethodsConfig;

  /**
   * Admin-managed registry of POS (Cashier) payment methods. Each entry pairs a
   * `tenderKind` (cash-with-change | exact non-cash | wallet debit) with an
   * optional wallet `currency`. The cashier reads enabled methods from
   * `GET /pos/payment-methods` and records the chosen method's code+label on
   * `pos_payments`. Defaults to the built-in Cash/Card/GC/Coin set (the former
   * hardcoded tenders) so POS works before any admin config. See
   * `pos-payment-methods.ts` for the shape.
   */
  pos_payment_methods: PosPaymentMethodsConfig;

  /**
   * Admin-managed list of POS custom fee TYPES. The cashier picks one per fee
   * line and types the amount; the server resolves the label from the code, so
   * a typo'd fee name is no longer representable.
   *
   * Defaults EMPTY — see `POS_CUSTOM_FEES_DEFAULT`. With no types configured the
   * fee block is hidden at the till entirely.
   */
  pos_custom_fees: PosCustomFeesConfig;

  /**
   * Global kill-switch for online payment gateways (Dragonpay / iPay88). When
   * off (default), the `POST /payments/gateway/initiate` route 404s even if a
   * `settlement:'gateway'` method is enabled in the registry — so an admin can't
   * expose a gateway that can't actually transact (e.g. before env creds are set).
   */
  gateway_payments_enabled: boolean;

  /**
   * BF1: admin-authored Terms of Service shown on signup. `terms_version` is
   * bumped whenever the text changes; a member whose `terms_version_accepted`
   * is below the current version is re-prompted on next login.
   */
  terms_text: string;
  terms_version: number;

  /**
   * BF1: Privacy Policy, tracked separately from Terms so each can be revised
   * (and re-consented) independently.
   */
  privacy_policy_text: string;
  privacy_policy_version: number;

  /**
   * BF1: when true, signup requires accepting the current Terms version and a
   * version bump re-prompts existing members at login. Defaults to FALSE so a
   * fresh tenant (empty `terms_text`) doesn't block signups before the admin
   * has authored terms — flip it on once the text is published.
   */
  require_terms_on_signup: boolean;

  /**
   * When true, `/auth/signup` requires a unique `username` (in addition to
   * email). Defaults to FALSE so existing email-only signups keep working.
   * The member signup form shows the username field + a live availability
   * check only when this is on.
   */
  require_username_on_signup: boolean;

  /**
   * Admin-defined extra fields collected on member signup (text inputs or file
   * uploads). Empty by default; the signup form renders them in `order` and the
   * values land on `users.registration_data`. See {@link RegistrationField}.
   */
  registration_fields: RegistrationField[];

  /**
   * Which fields a member may edit on their Profile tab (name / phone / photo).
   * Enforced server-side by `PATCH /me`. See {@link MemberEditableFields}.
   */
  member_profile_editable: MemberEditableFields;

  /**
   * Which fields a merchant may edit on their Profile page (business name /
   * owner name / contact number / contact email / pickup address). Enforced
   * server-side by `PATCH /merchant/me`. See {@link MerchantEditableFields}.
   */
  merchant_profile_editable: MerchantEditableFields;

  /**
   * When true, `/auth/login` accepts a username OR an email in the identifier
   * field for member logins. Defaults to FALSE (email-only). Admin + cashier
   * logins are always email-only regardless of this flag. Independent of
   * `require_username_on_signup` so a tenant can collect usernames as identity
   * without enabling username login.
   */
  allow_login_by_username: boolean;

  /**
   * Activation terms shown at slot activation (kit redeem). Mirrors the BF1
   * Terms pattern: `activation_terms_version` is bumped when the text changes,
   * and the accepted version is recorded on the slot. The classic use is the
   * "activation cannot be changed once done" agreement.
   */
  activation_terms_text: string;
  activation_terms_version: number;

  /**
   * Admin-editable success message shown to the member right after a slot is
   * activated (kit redeem). Plain text; returned by `/kits/redeem` so the member
   * SPA can surface it in the activation success toast/modal. Default
   * "Congratulations! Your slot is now active."
   */
  activation_success_message: string;

  /**
   * When true, `/kits/redeem` requires the member to accept the current
   * `activation_terms_version` before a slot is activated. Defaults to FALSE so
   * a tenant with empty activation terms never blocks redeems.
   */
  require_activation_terms: boolean;

  /**
   * Storefront: congratulatory message shown in the "Order placed!" dialog after
   * a successful checkout. Admin-editable; exposed via the public
   * `GET /tenant/config` so the storefront can render it.
   */
  order_success_message: string;

  /**
   * Member portal: message shown in the dialog that greets a member on login when
   * they have one or more unpaid orders (status pending/to_pay). Admin-editable;
   * exposed via the public `GET /tenant/config`.
   */
  unpaid_orders_dialog_message: string;

  /**
   * Admin-configurable default sponsor for self-signups without a `?ref=` code.
   * The user_id (NOT a referral code) of an active member; that user becomes the
   * new member's `sponsor_user_id`. When `null` (default), signup falls back to
   * super_admin (legacy Phase 10 behavior). Setting this lets a tenant tag all
   * direct registrations under a marketing-leader account instead of root.
   *
   * LEGACY: superseded by `default_sponsor_slot_code` (a specific slot, not a
   * user). Kept as a fallback so already-configured tenants keep working — at
   * signup the slot_code is preferred, then this user, then super_admin.
   */
  default_sponsor_user_id: string | null;

  /**
   * Walk-in default sponsor SLOT for self-signups without a `?ref=` code — the
   * slot_code of an active slot. The new member registers directly under this
   * slot (both `sponsor_user_id` and `registered_under_slot_id` are set from
   * it). `null` (default) falls back to `default_sponsor_user_id`, then
   * super_admin. Preferred over the legacy user-level field because it removes
   * the "which of the member's slots?" ambiguity. Resolved + re-validated
   * active at use time via `resolveReferralSlot`.
   */
  default_sponsor_slot_code: string | null;

  /**
   * Global override for LINK registrations (signups that arrive with a `?ref=`
   * code). When set to an active slot_code, EVERY link signup is routed under
   * this slot regardless of which link was used. The original link is still
   * counted as used (`incrementReferralUse`) and the member is still
   * `referred_via_link`. `null` (default) = normal per-link behavior (register
   * under the slot the link resolves to).
   */
  link_sponsor_override_slot_code: string | null;

  /**
   * When true (default), a member's SECOND-and-later slots suggest the member's
   * OWN first-created slot as the sponsor (self-stacking) instead of the
   * original signup sponsor. This is only a pre-filled suggestion — the member
   * can still type a different sponsor at redeem. A member's FIRST slot always
   * uses the registration sponsor regardless of this flag.
   */
  extra_slots_self_sponsor: boolean;

  /**
   * When true (DEFAULT), a member's SECOND-and-later slots default to the
   * SPECIFIC slot they registered under (`users.registered_under_slot_id` — the
   * replicated link's slot, or the walk-in `default_sponsor_slot_code` slot)
   * instead of falling back to the signup sponsor's OLDEST active slot.
   *
   * Only consulted while `extra_slots_self_sponsor` is OFF — self-stacking still
   * wins when it is on — and skipped when the registration slot is no longer
   * active. It matters only for MULTI-SLOT sponsors: with one slot, the
   * registration slot and the sponsor's primary are the same slot.
   *
   * Turning it OFF restores the legacy behavior (the signup sponsor's oldest
   * active slot), which silently detached a recruit's extra slots from the leg
   * whose link brought them in. Governs the DEFAULT only: a member who types a
   * sponsor (where `sponsor_editable_extra_slots` allows it) still overrides it.
   */
  extra_slots_use_registration_slot: boolean;

  /**
   * Sponsor-editability at slot activation/redeem. Each governs whether the
   * member may OVERRIDE the pre-filled sponsor (by typing a `sponsorCode`) for a
   * distinct, non-overlapping activation domain. All default `true` (editable —
   * current behavior); flipping one to `false` LOCKS that domain's sponsor to the
   * computed default (an admin `sponsor_override_slot_id` still wins over all).
   *  - `sponsor_editable_from_replicated_link`: the member's FIRST slot when they
   *    registered via a replicated/referral link (`users.referred_via_link`).
   *    Locked default = `users.registered_under_slot_id`.
   *  - `sponsor_editable_extra_slots`: a SECOND-or-later slot while
   *    `extra_slots_self_sponsor` is OFF (default = the member's registration
   *    slot when `extra_slots_use_registration_slot` is on, else the signup
   *    sponsor's primary).
   *  - `sponsor_editable_self_stack`: a SECOND-or-later slot while
   *    `extra_slots_self_sponsor` is ON (self-stack; default = member's own slot).
   * A first-slot WALK-IN (no link) is governed by none of these — always editable.
   */
  sponsor_editable_from_replicated_link: boolean;
  sponsor_editable_extra_slots: boolean;
  sponsor_editable_self_stack: boolean;

  /**
   * Restrict the leader-redeem paths (`POST /me/codes/redeem-for-downline`,
   * `POST /me/leads/:userId/activate`) to the caller's DIRECT downline —
   * `users.sponsor_user_id` pointing at the caller, not merely somewhere in
   * their sponsor chain.
   *
   * Those routes force the CALLER as the new slot's sponsor, overriding the
   * recipient's own `registered_under_slot_id`. At any depth that lets a leader
   * activate someone who joined through a DIFFERENT downline's replicated link
   * and silently move them onto the leader's leg, taking the activation comp
   * with them.
   *
   * Default ON. It removes no capability: transferring the code
   * (`POST /me/codes/:codeId/transfer`, `kit_codes_downline_transfer`) still
   * reaches any depth, and it is the SAFER route — a transfer creates no sponsor
   * edge, so the recipient redeems under their own registration sponsor.
   */
  redeem_for_downline_direct_only: boolean;

  /**
   * When true, the member dashboard's "Your standing" card shows the member's
   * sponsor (the owner of their active slot's `sponsor_slot_id`). Default
   * `false` (hidden) — display-only, no behavior change.
   */
  show_sponsor_on_dashboard: boolean;

  /**
   * Which detail fields render on each box of the BINARY genealogy tree (member
   * + admin). See {@link BinaryGenealogyNodeFields}. Display-only.
   */
  binary_genealogy_node_fields: BinaryGenealogyNodeFields;

  /**
   * Platform/domain license expiration as an ISO date string ("YYYY-MM-DD"), set
   * by the super-admin. Drives the admin renewal-reminder popup, which appears
   * on login within 3 months of (or past) this date. null = unset (no reminder).
   * Notification-only — it does NOT lock the tenant out.
   */
  domain_expires_at: string | null;

  /**
   * When true (default), the slot-activation/redeem form shows the sponsor
   * field. When false, the sponsor field is HIDDEN on redeem for members who
   * have an active admin sponsor override (`users.sponsor_override_slot_id`) —
   * a "stealth" mode so an overridden member can't see they were overridden.
   * Members WITHOUT an override always see the field regardless of this flag.
   */
  show_sponsor_on_redeem: boolean;

  /**
   * Gates who may register. `'on'` (default) = open registration via the signup
   * page and links. `'links_only'` = registration is allowed ONLY when the user
   * arrived through a referral or product-referral link (a `?ref=` sponsor code);
   * the standalone signup page without a link is rejected. `'by_sponsor_only'` =
   * the standalone signup page requires the user to supply a sponsor code.
   * Enforced server-side in `/auth/signup`; the member signup page reads it from
   * the public `GET /tenant/config` to adapt its form.
   */
  signup_mode: 'on' | 'links_only' | 'by_sponsor_only';

  /**
   * INFORMATION-SYSTEMS-ONLY mode: the public site becomes a pure marketing
   * brochure — no membership, no users, no shop. Just the tenant's own landing
   * pages, with login / signup / cart / search / account gone from the chrome.
   *
   * The member SPA mounts a REDUCED route tree (the bespoke landing + its own
   * pages only), so `/login`, `/signup`, `/cart`, `/checkout` and the whole
   * member portal are unreachable rather than merely hidden. `/impersonate`
   * stays mounted: it needs an admin-issued single-use token, and without it a
   * super-admin has no way onto the box's member side at all.
   *
   * Server-side, `/auth/signup` rejects outright and `/auth/login` rejects
   * member-only users arriving from the MEMBER origin — admin / cashier /
   * merchant SPAs sit on their own origins and are untouched, and an admin may
   * still log in on the member origin as a second escape hatch.
   *
   * REQUIRES a bespoke landing (`hasBespokeLanding`, enforced in
   * `PATCH /admin/tenant/features`) — otherwise `/` would render nothing.
   * Default false: the storefront is the norm.
   */
  public_site_info_only: boolean;

  /**
   * Storefront home de-duplication: what the `productFeed` grid ("Just For You")
   * leaves out because the same product is already headlined higher up the page.
   *
   * Both default FALSE = the grid lists the whole visible catalog, which is the
   * behavior every tenant had before these existed. They are opt-in per tenant
   * on purpose: `products.featured` has only ever been an ORDER BY boost, so
   * clients have ticked it liberally (one live tenant has it on 25 of 28
   * products) and flipping its meaning globally would empty their home page.
   *
   * Neither affects Shop, search, category pages or a direct link — a hidden
   * product stays findable everywhere else. This is page composition, not
   * visibility.
   */
  product_feed_hide_featured_packages: boolean;
  product_feed_hide_featured_products: boolean;

  /**
   * When true, a visitor who arrived through an ENABLED campaign (replicated)
   * page satisfies the restrictive `signup_mode`s — the page IS the invitation.
   * They carry no `?ref=`, so they register under `default_sponsor_slot_code`
   * like any other walk-in. This is the ref-less `/c/<slug>` funnel a virtual
   * product opens; without it that funnel dead-ends on a `links_only` /
   * `by_sponsor_only` tenant. The slug is validated server-side against an
   * enabled `replicated_pages` row — never trusted from the client. Default
   * false: invitation-only is a deliberate tenant choice, so widening it is
   * opt-in. No effect when `signup_mode` is `'on'`.
   */
  campaign_page_signup_allowed: boolean;

  /**
   * When true, members may set/change their own referral code from Settings
   * (subject to `referral_code_max_changes`). Default false (opt-in). Custom
   * codes resolve to the member as sponsor at signup exactly like the
   * auto-generated code.
   */
  member_referral_code_self_set: boolean;

  /**
   * Max number of times a member may change their referral code (when
   * `member_referral_code_self_set` is on). `0` disables changes; admins can
   * reset a member's counter to grant more. Default 1.
   */
  referral_code_max_changes: number;

  /**
   * When true, visiting a member's replicated/referral link shows the
   * admin-built landing page (a stack of typed blocks) BEFORE signup, instead
   * of dropping straight onto the signup form. The referral code in the URL
   * still drives sponsor attribution (the page's CTA carries it to signup).
   * Default false — the link keeps its current straight-to-signup behavior.
   */
  replicated_page_enabled: boolean;

  /**
   * When true, clicking a VIRTUAL product in the online store opens the
   * replicated page assigned to it (`products.replicated_page_id`, picked in
   * the admin product editor) instead of the normal product page — so a
   * service/virtual offer sells through its campaign landing page rather than a
   * cart-first product page.
   *
   * Narrow by design: only virtual products with an ENABLED page assigned are
   * redirected. Anything else — the flag off, a physical product, no page
   * assigned, a disabled or deleted page — keeps `/product/:id`. The canonical
   * product URL is never redirected either, so the Dropshipping share link
   * (`/product/:id?ref=`) and search results still work. Default false.
   */
  virtual_products_open_replicated_page: boolean;

  /**
   * Master switch for the VIRTUAL PRODUCTS feature in the ADMIN UI. When off
   * (default), nothing about virtual products is offered anywhere in admin: the
   * Virtual Products page and its sidebar entry are gone, the product editor
   * shows no "Virtual product / service" flag, and the two virtual policy
   * toggles above are hidden. A tenant that only sells physical goods never
   * meets the concept.
   *
   * DELIBERATELY UI-ONLY — it does NOT change runtime behaviour. A product
   * already carrying `is_virtual` keeps skipping shipping, dimensions and
   * inventory exactly as before, because silently re-introducing a courier fee
   * (or an inventory requirement) on a live service because someone flipped a
   * display toggle would be a money bug, not a preference. Those rows also stay
   * visible in the ordinary Products list while the flag is off, so they can
   * still be edited — they're just no longer split into their own catalog.
   *
   * Flip it on and everything appears; flip it off and it's hidden again. No
   * data is written or migrated either way.
   */
  virtual_products_enabled: boolean;

  /**
   * Master admin-UI switch for MATERIALS and RECIPES. A material is stock that
   * is never sold (cups, lids, coffee beans); a product's recipe names the
   * materials one sold unit uses up, and selling the product consumes them.
   * While off (the default) the admin has no Materials page and the product
   * editor shows no Recipe tab, so a tenant that only resells finished goods
   * never meets the concept.
   *
   * UI-ONLY, the same rule as `virtual_products_enabled`: a recipe that already
   * exists keeps consuming its materials at sale while this is off. Silently
   * stopping consumption because someone flipped a display toggle would leave
   * the shelf counts wrong with no trace of why.
   */
  materials_enabled: boolean;

  /**
   * STOCK LOTS: every receipt of stock becomes a lot carrying its own cost and
   * (optionally) expiry date, a stock-out records which lots it took from, and a
   * sale is costed at what its units actually cost instead of the product's
   * static cost. Also turns on the admin Lots / Expiring views and the lot
   * fields on the receive form.
   *
   * Unlike `materials_enabled` this is a BEHAVIOUR switch, read inside
   * `applyInventoryDelta`. While off (the default) no lot is written or
   * consumed and every stock path is exactly what it was. Lots are additive:
   * `inventory.qty` stays the availability source of truth, and stock a branch
   * held before the switch has no lot (it is costed at the static cost and goes
   * out first). Turning it off again stops the bookkeeping, so lots recorded
   * earlier go stale; it is meant to be turned on once.
   */
  inventory_lots_enabled: boolean;

  /**
   * Which lot a stock-out takes from first, while `inventory_lots_enabled` is
   * on. `fifo` (default) = oldest received first. `fefo` = the lot that expires
   * soonest first, then the rest in received order.
   */
  lot_pick_order: 'fifo' | 'fefo';

  /**
   * ITEM LOCATOR: record where each product is kept in a branch (aisle, then
   * rack or shelf) and show it on the admin branch stock list, the stock-lot
   * views and the cashier's Inventory page.
   *
   * UI-ONLY and off by default: it adds a column to every branch's stock list,
   * so an upgrade must not show it to a tenant that never asked. A location
   * already saved is simply hidden while this is off.
   */
  item_locator_enabled: boolean;

  /**
   * GIFT CHECKS (internally `vouchers`): admin-defined programs that give a
   * member a one-use discount after buying N units of chosen products, or by
   * hand. The member sees them in the member app with a code and a QR; the
   * cashier applies one at checkout.
   *
   * A BEHAVIOUR switch and off by default: while off no order counts toward a
   * gift check, none can be redeemed, and the admin page, the member page and
   * the till buttons are hidden. Gift checks already issued are kept and come
   * back when it is turned on again.
   */
  vouchers_enabled: boolean;

  /**
   * What the tenant calls a gift check everywhere a person reads it (admin,
   * member app, till, receipt). Default "Gift Check". The code and the API say
   * `voucher` so it never collides with "GC", which here is the Gift Cert WALLET.
   */
  voucher_label: string;

  /**
   * Members can claim a gift check of every active `birthday` program once per
   * birthday, from the birthday itself through this many days after it (0 = the
   * day only). Counted in the tenant's timezone. Cashier phase 5.
   */
  birthday_claim_window_days: number;

  /**
   * Only a member whose KYC is verified may claim a birthday gift check, since
   * the birth date is only trustworthy once someone checked it against an ID.
   * On by default; a tenant that does not run KYC turns it off.
   */
  birthday_requires_kyc: boolean;

  /**
   * Where a member's birth date is asked for. `hidden`: nowhere a member sees
   * (an admin can still record it). `optional` / `required`: on the signup form
   * (required or not) and on the member's profile, where they can change it
   * until their KYC is verified. Birthday gift checks need it.
   */
  birth_date_field: BirthDateFieldMode;

  /**
   * Create a free slot for every member who signs up (see
   * `SignupFreeSlotConfig`). Off by default: on an MLM tenant a slot is what a
   * kit buys, so handing one out at signup is a loyalty-tenant choice.
   */
  signup_free_slot: SignupFreeSlotConfig;

  /**
   * Cashiers holding `pos.topup` can load a member's cash wallet at the till,
   * paid in cash (it goes in the drawer) or by an exact method such as a card.
   * Off by default. It credits exactly what an approved top-up request does:
   * the same brackets, bonuses and gift checks.
   */
  pos_topup_enabled: boolean;

  /**
   * Also let a till top-up be paid by an EXACT method (a card, an e-wallet),
   * with its reference required. Off by default: a cash top-up is checked by
   * the drawer count at shift close, but nothing at the till checks a card or
   * e-wallet one, and wallet cash can be cashed out, so a tenant opts in and
   * reconciles those against the provider's records.
   */
  pos_topup_exact_methods_enabled: boolean;

  /**
   * A till top-up of this many pesos or more needs a manager's PIN (someone
   * holding `pos.topup.approve`), given for that slot and that amount. Null =
   * never. A guard on the cashier's word for large loads, since wallet cash
   * can be cashed out.
   */
  pos_topup_pin_above: string | null;

  /**
   * Member Resources library: admin-published documents (PDF / XLSX / DOCX)
   * and external links (this is how video is carried — see the `resources`
   * schema note) that members can view and download.
   *
   * Off by default so an existing tenant's member portal gains no new nav item
   * on upgrade. UI/route gating only: turning it off hides the member page and
   * the admin CMS, it does not delete anything, and stored files are untouched.
   */
  member_resources_enabled: boolean;

  /**
   * Render Office documents (xlsx / docx / pptx) inline in the member Resources
   * viewer, via Microsoft's public Office Online viewer.
   *
   * PRIVACY TRADE-OFF, which is why this is its own opt-in flag rather than part
   * of `member_resources_enabled`: those formats have no native browser
   * renderer, so showing one in-app means handing a short-lived signed link to
   * Microsoft, whose servers fetch and render the file. The document leaves the
   * tenant's infrastructure at view time. Off by default; with it off, Office
   * files stay download-only. PDFs and video links are unaffected either way —
   * they render natively and never leave.
   */
  member_resources_office_viewer: boolean;

  /**
   * LMS MASTER SWITCH. Off (default) = the whole learning system does not
   * exist for this tenant: every course / creator / creator-order API route
   * 404s (including the PUBLIC creator registration), the admin Creators page
   * + Settings → Learning card are hidden (a tenant admin must never see it),
   * the member /my-courses + /learn routes and nav are absent, the learning
   * dashboard mode falls back to 'mlm', and the creator SPA refuses to load.
   * `member_dashboard_mode` etc. below are only meaningful when this is on.
   */
  lms_enabled: boolean;

  /**
   * LMS: which member dashboard the portal renders.
   *  - 'mlm'      — the passbook dashboard (wallet, standing, PV, slots…);
   *  - 'learning' — the "Classroom" dashboard (Jump back in hero, catalog
   *                 strip, invite card, announcements); the nav relabels
   *                 Store→Catalog, Training→My Courses, Earnings→Rewards and
   *                 hides the MLM-only items (Genealogy, Vault, Redeem, Leads,
   *                 Live Links) — see design_handoff_classroom_dashboard.
   * Default 'mlm' — existing tenants are byte-identical.
   */
  member_dashboard_mode: 'mlm' | 'learning';

  /**
   * LMS learning mode: show the "Invite a friend" card (referral link +
   * joined/rewards stats) on the classroom dashboard. Off = the Announcements
   * card spans full width and course buyers see no re-selling pitch. Default
   * true (course buyers can re-sell).
   */
  show_team_earnings: boolean;

  /**
   * LMS: size cap (MB) for an R2-hosted lesson video uploaded through the
   * presigned-PUT path. Signed into the upload URL and re-checked at
   * finalize. Videos never transit the API, so this is a storage-cost knob,
   * not a heap-safety one. Default 1024 (1 GiB).
   */
  course_video_max_mb: number;
  /**
   * Phase 9 — the storage lifecycle sweep. When false the sweep is a no-op:
   * nothing is scanned and nothing is deleted, but writers keep ENQUEUEING, so
   * turning it back on later still finds everything that was orphaned while it
   * was off. Off is therefore a pause, never a data loss.
   */
  storage_sweep_enabled: boolean;
  /**
   * Hosts a lesson embed URL may point at. A creator-supplied URL is rendered
   * in an iframe INSIDE the member portal, so an unrestricted list is a
   * third-party page running in a place learners trust. Defaults to the two
   * providers whose players we can actually drive; a direct media file over
   * https is always allowed regardless (it plays in our own <video>).
   */
  course_embed_hosts: string[];
  /**
   * Days an orphaned object sits in `storage_deletions` before the sweep may
   * delete it. The delay is the safety feature: a mistaken edit, a rolled-back
   * deploy or a restored database dump has this long to put the reference back,
   * and the sweep re-checks references at delete time. 0 means delete on the
   * next tick — only sensible on a throwaway box.
   */
  storage_delete_grace_days: number;

  /**
   * LMS Phase 2: days of inactivity before a learner receives ONE in-app
   * `learning_reminder` naming their most recently touched unfinished course.
   * Activity is judged across ALL of a learner's live enrollments, so someone
   * studying anything is never nudged. `null` or `0` turns reminders off.
   * Only meaningful while `lms_enabled`.
   */
  lms_nudge_after_days: number | null;

  /**
   * LMS: how the POS handles a cart containing a COURSE product.
   *
   * A till sale always books to a chosen member account — the cashier cannot
   * ring a truly anonymous walk-in — so the risk is not "nobody gets access",
   * it is that access lands on whatever account was selected (commonly a
   * shared house/walk-in member), and the buyer never gets the course.
   *  - 'allow' — no special handling;
   *  - 'warn'  — the cashier sees whose account will receive access, inline
   *              and again in the review modal (DEFAULT);
   *  - 'block' — course products cannot be sold at the till at all
   *              (`course_sales_disabled_at_pos`); the member buys online.
   * Recovery for a mis-rung sale is the admin grant/revoke pair on the
   * Enrollments page, whatever this is set to.
   */
  pos_course_sale_mode: 'allow' | 'warn' | 'block';

  /**
   * LMS Phase 8: what happens when a creator changes PLAYABLE content (a video
   * swap, an embed URL, an article body, a lesson added or deleted) on a course
   * whose listing an admin already approved.
   *
   * Before this existed the answer was "nothing": a ₱1 price edit bounced the
   * whole listing out of the catalog, while replacing 100 % of the video a
   * course sells went live instantly, unreviewed and unannounced.
   *  - 'hold'   — **DEFAULT.** The course keeps selling AND the enrolled
   *               learner keeps the version an admin last approved: the
   *               creator's new video, embed link or article body is STAGED in
   *               `course_lesson_held_sources` and goes live only when the
   *               content decision is Approve (Reject discards it). Only a
   *               single lesson's playable source is holdable
   *               (`HOLDABLE_CHANGE_KINDS`); a lesson added or deleted, a
   *               chapter deleted, titles, ordering, drip and tests still land
   *               live and merely flag, because staging the SHAPE of a
   *               curriculum leaves a mid-course learner nothing coherent to
   *               be shown.
   *  - 'flag'   — the course keeps selling and the change is queued for review
   *               with a per-lesson change list and an owner alert, but the new
   *               content is ALREADY LIVE for everyone who bought the course
   *               while it waits. This was the default until 2026-09-11; a
   *               tester reported the consequence (a swapped video reached
   *               buyers while the course read "For review") and the user chose
   *               to make holding the standing behaviour instead;
   *  - 'delist' — the listing bounces to `pending` exactly like a price edit,
   *               so nothing new sells until a human has looked. The new
   *               content is live for existing buyers, as in 'flag'.
   *
   * The distinction that matters: 'flag' and 'delist' govern who can BUY while
   * a change is unreviewed, and neither has ever touched what an existing
   * learner plays. 'hold' is the only one that governs what the learner SEES,
   * and it does so without taking anything away — the approved version keeps
   * playing rather than the lesson going dark.
   *
   * Changing this away from 'hold' does not release anything already staged:
   * held rows are applied or discarded by a content DECISION, so an admin still
   * has to rule on what is waiting.
   * Only meaningful while `lms_enabled`.
   */
  lms_content_rereview: 'flag' | 'delist' | 'hold';

  /**
   * LMS Phase 10 — how MUCH of a creator's editing re-opens that review.
   *
   * `content` is the Phase-8 scope: only tier-1 playable content (a swapped
   * video, a deleted lesson) sends an approved course back to a human.
   * `all` also gates tier 3 — titles, descriptions, prices, drip, chapter and
   * lesson ordering, tests, translation links — which is what a tenant means by
   * "nothing changes on a live course without me seeing it first". Attachments
   * and captions (tier 2) stay non-gating under both, or the queue becomes
   * noise nobody reads.
   *
   * Defaults to `all`: the client who asked for change review asked for it on
   * even minor edits, and the narrower scope stays one Select away.
   * Only meaningful while `lms_enabled`.
   */
  lms_content_rereview_scope: 'content' | 'all';

  /**
   * LMS Phase 8: member-facing abuse reporting on courses, reviews, Q&A and
   * announcements, plus the admin moderation queue that receives them.
   * Defaults ON (a safety valve is not an upsell), but it is still a toggle
   * because a tenant running a closed house-only catalog has nobody to report
   * anybody to. Hidden entirely while `lms_enabled` is off.
   */
  lms_abuse_reporting_enabled: boolean;

  /**
   * When true, a learner cannot seek past the furthest point they have actually
   * watched, until they reach the end of the lesson once — after which that
   * lesson is freely scrubbable forever.
   *
   * The mark is CLIENT-reported (`course_lesson_progress.max_position_seconds`),
   * so this is a UX rule, not DRM: it stops idle skipping, not a determined
   * person with the network tab open. Default OFF, because it is a real
   * restriction on people who paid and most tenants do not want it.
   */
  lms_seek_restriction: boolean;

  /**
   * When false, the player's "Mark complete" button is gone: a video lesson
   * completes only from playback (the 90 % rule) and an article when the
   * learner scrolls to its end. The one exception is an embed the player
   * cannot track (an unknown host, a domain-locked Vimeo), which keeps the
   * button, because nothing else could ever complete it.
   *
   * Like the seek restriction this is a UX rule, not enforcement: the
   * complete endpoint still accepts `via: 'manual'`. Default ON, which is how
   * every tenant has always worked.
   */
  lms_manual_completion: boolean;

  /**
   * Whether someone who is NOT signed in may browse the courses: the
   * catalogue, a course's page and the instructor list, on the website and in
   * the coach app. Off, those routes answer 401 `sign_in_required` to an
   * anonymous caller and the public product feed leaves course products out,
   * so it is a real gate and not a hidden menu. Default ON, which is how the
   * website has always worked.
   */
  lms_public_catalog: boolean;

  /**
   * Coach PHONE APP only: a course the member cannot open yet gets a button
   * that opens the same course on the website, where it can be bought ("Join
   * Miracles Protocol" for a visitor, "Continue on the website" for a member).
   * Default OFF on purpose: Google Play's payments policy forbids leading the
   * users of a Play-distributed app to another way of paying for digital
   * content, so switching this on is the tenant accepting that risk. Off, the
   * app says nothing about where to buy, like Success Pro on /upgrade.
   */
  lms_app_web_link: boolean;

  /**
   * LMS Phase 10 — WHO sells a subscription, and therefore what
   * `access_level = 'subscriber'` means.
   *
   * `tenant` (default) is the legacy model: one tenant-wide monthly wallet
   * subscription (`member_subscriptions`, the special-links plan) unlocks every
   * subscriber course in the catalog. `creator` moves the sale to the AUTHOR:
   * each creator offers monthly / 6-month / 12-month plans, and a subscriber
   * course is unlocked by a live period for THAT creator only.
   *
   * Left defaulting to `tenant` because flipping it silently re-gates every
   * subscriber course a tenant is already selling.
   */
  lms_subscription_model: 'tenant' | 'creator';

  /**
   * May a member with no activated slot buy a creator subscription?
   *
   * Defaults to `activated_slot`, which is what the legacy wallet subscription
   * already required (a slot wallet pays for it). `any_member` opens it to any
   * signed-up member, which is what a tenant selling courses to the public
   * wants. Enforced live in BOTH checkout guards and never mirrored onto
   * `products.sold_to` — a bulk column update driven by a settings PATCH would
   * be a second source of truth that drifts the moment either side is edited.
   */
  lms_subscription_eligibility: 'activated_slot' | 'any_member';

  /**
   * Under the CREATOR subscription model, may a member opt in to renewing a
   * plan automatically from a slot wallet? Default on. Off: the renewal engine
   * charges nothing and moves nothing, the opt-in disappears, and members get
   * the "ends in 3 days" reminder with a Renew now button instead.
   */
  lms_subscription_auto_renew_enabled: boolean;

  /**
   * Under the CREATOR subscription model, does paying a HOUSE plan by Stripe
   * card start a Stripe SUBSCRIPTION (Checkout in subscription mode) instead of
   * buying one period? Default off. On: each invoice Stripe collects becomes a
   * real renewal order that stacks the next window, and the member manages the
   * card in the Stripe Customer Portal from My Courses. Manual Pay, Dragonpay
   * and iPay88 always buy one period. Turning it off only affects new
   * checkouts: subscriptions already open keep billing until they are
   * cancelled in Stripe or here.
   */
  lms_subscription_card_recurring: boolean;

  /**
   * Does a slot's OWN personal PV (PPV) count toward its OWN group PV (GPV)?
   * Default `true` — GPV = own PPV + all descendants' PPV (the historical
   * behavior). When `false`, a slot's GPV reflects only its downline's volume;
   * the buyer's own purchase bumps PPV only, never its own GPV. Affects every
   * GPV-based gate (e.g. Stairstep `min_gpv`). Ancestors are unaffected.
   */
  gpv_includes_ppv: boolean;

  /**
   * F6 — Product Code Generation. When `true`, a PV-bearing purchase does NOT
   * roll up PV (PPV/GPV) at completion; instead the worker mints a PV-carrying
   * "product code" into the buyer's Code Vault. The buyer redeems it to release
   * the deferred PV (credited to the slot that bought — captured at purchase).
   * COMMISSIONS are unaffected (they still pay at purchase); only PV — and the
   * PV-driven live rank-up — is deferred. Kit orders are exempt (they mint their
   * own activation codes). Default `false` (PV rolls up immediately as before).
   */
  product_code_generation: boolean;

  /**
   * Stairstep payout-base policy. When `true`, a slot's OWN personal PV is
   * EXCLUDED from its Stairstep commissionable base — only downline (group)
   * legs' PPV drive the differential. Default `false` (the slot also earns on
   * its own PPV, the historical behavior + reference-impl parity). Rank
   * QUALIFICATION is unaffected — this only changes the payout base.
   */
  stairstep_exclude_personal_pv: boolean;

  /**
   * What per-month PV history members see on their dashboard. `'off'` hides the
   * card, `'ppv_only'` shows monthly personal PV, `'ppv_and_gpv'` (default) adds
   * group PV. Read-only display policy — does not affect any computation.
   */
  member_pv_display: 'off' | 'ppv_only' | 'ppv_and_gpv';

  /**
   * Dropshipping Income: master switch for the per-product replicated-link
   * retail-margin earning. When on, members can share a product link
   * (`/product/{id}?ref={code}`); an outside buyer who registers and buys
   * through it pays full SRP, becomes a lead of the sharer, and the sharer earns
   * `(SRP − sharer's own discounted price) × qty` per discounted line via the
   * `dropshipping_income` comp plan. Default false (opt-in).
   */
  dropshipping_income: boolean;

  /**
   * Store-wide Share & Earn link (`/s/{ref}`): landing on it stores the
   * sharer's ref and drops the lead on the storefront, so EVERYTHING they buy
   * in that visit is attributed like a per-product share — same SRP pricing,
   * same margin math, same single `dropshipping_income` ledger credit (summed
   * across lines), same attribution-ends-at-the-order lifetime.
   *
   * Its own flag rather than riding `dropshipping_income` because the exposure
   * differs materially: a product link taints one product, a store link taints
   * the whole catalog for the visit. This flag governs the SHARE SURFACE only
   * (the member UI + whether `/s/:ref` stores the ref) — money authority stays
   * with `dropshipping_income`, enforced server-side on the dropship-orders
   * route. REQUIRES `dropshipping_income` to actually pay. Default false.
   */
  store_wide_dropship_link: boolean;

  /**
   * Pay the inviter on a "register, then pay" funnel sale. When on, a visitor
   * who reaches `/buy/:productId` through someone's campaign link checks out as
   * a DROPSHIP sale attributed to that sharer's slot, so the inviter earns the
   * retail margin on a product the buyer bought before ever activating.
   *
   * Without it a funnel sale pays nobody: the buyer holds no slot, so
   * `orders.buyer_slot_id` is null and the comp gate skips the order entirely.
   * `users.sponsor_user_id` records who introduced them but carries no money.
   *
   * REQUIRES `dropshipping_income` — this reuses that mechanism rather than
   * inventing a second one, and inherits its semantics wholesale:
   *  - the buyer pays SRP (that margin IS the inviter's income), and
   *  - the order settles by manual proof, never a wallet or gateway.
   * With Dropshipping Income off, the funnel still works; the sale is simply
   * unattributed, exactly as it is today.
   *
   * Attribution is session-scoped, like every other dropship link: a lead who
   * comes back later without the ref places an ordinary, unattributed order.
   * Default false.
   */
  virtual_funnel_dropship_attribution: boolean;

  /**
   * Replicated-link PV to owner: when on, a non-activated buyer's SRP purchase
   * through a replicated link credits the link OWNER's PV as if the owner bought
   * it (owner PPV + owner's upline GPV rollup); the buyer's own PV is untouched.
   * Independent of `dropshipping_income` (the cash retail-margin switch) — either
   * may be on alone. Default false (opt-in).
   */
  replicated_link_pv_to_owner: boolean;

  /**
   * When on, the shareable replicated link emits an OPAQUE encrypted ref token
   * (`?ref=e1.<base64url>`) instead of the plaintext referral code. Resolution
   * is always backward-compatible — both forms are accepted regardless of this
   * flag — so flipping it never breaks links already shared. Default false (the
   * link keeps exposing the human-readable code).
   */
  replicated_link_ref_encrypted: boolean;

  /**
   * BF4: when true, orders paid in a NON-cash currency (gc / coin) fire the
   * `order_completed` comp event like a cash sale — uplines earn commissions
   * and the buyer's product `coin_grant` is granted. Defaults to FALSE: a
   * non-cash order fires NO comp at all (no commissions, no points/tokens, no
   * coin-grant — avoids a coin-farming loop). Cash orders are never affected.
   */
  commissions_on_non_cash: boolean;

  /**
   * BF4: when true, an ecom order settled from a WALLET (gc / coin / cash-wallet)
   * that has a SHIP-TO ADDRESS quotes shipping normally, folds the peso courier
   * fee into the order's own currency 1:1 (one combined `checkout-<orderId>`
   * wallet debit covers goods + shipping), and walks the real fulfilment ladder
   * (paid → to_ship → shipping → delivered → completed) instead of
   * instant-completing. Pickup / address-less wallet orders are UNCHANGED:
   * instant complete, zero shipping.
   *
   * **Default TRUE since 2026-09-09.** Scrip still buys PHYSICAL goods, and a
   * GC order that completes the instant it is placed leaves nothing to pick,
   * pack or hand over — the parcel was owed either way. Turning it off restores
   * the old behaviour: a wallet order can't carry shipping
   * (`shipping_not_allowed_for_non_cash`) and completes at checkout.
   *
   * Flipping this default does NOT reach a tenant whose config already stores
   * the key — `resolveFeatures` only fills in ABSENT keys, and every live box
   * predating this stores `false` explicitly. Those need the admin toggle (or a
   * config write) to pick the new behaviour up.
   *
   * NOTE: with a ladder GC order, kit codes and
   * commissions follow `kit_code_reveal_on` / `ecom_event_on` rather than firing
   * at checkout, and a gc/coin order still pays uplines only when
   * `commissions_on_non_cash` is also on.
   */
  wallet_orders_charge_shipping: boolean;

  /**
   * When true, a wallet-settled ecom order for PHYSICAL goods completes the
   * instant it is placed. Default FALSE.
   *
   * Payment being instant does not mean the sale is finished: the buyer still
   * has to collect the goods. An auto-completed pickup order shows as done
   * while the box is still on the shelf, which leaves the counter no worklist
   * and the member no way to tell what is waiting for them. Off, the order
   * stops at `paid` and walks the ordinary pickup ladder (to_pick → completed)
   * exactly like a cash sale.
   *
   * ALL-VIRTUAL orders ignore this and always complete at checkout — a course
   * has nothing to hand over, and holding one hostage would strand the
   * learner's enrolment. Unrelated to `wallet_orders_charge_shipping`, which
   * governs the SHIP channel and whether freight is charged.
   */
  wallet_orders_auto_complete: boolean;

  /**
   * When true, an ALL-VIRTUAL cart checks out on a stripped, nav-less screen and
   * goes straight to the payment page once the order exists, instead of the full
   * storefront checkout followed by a success dialog.
   *
   * The point is intent: someone who clicked "Enrol now" on a campaign page is
   * three clicks from paying, and every one of those clicks currently sits in a
   * page ringed with a shop header, a cart and a nav bar. A virtual cart already
   * has nothing to configure — no address, no courier, no shipping fee, no
   * quantity — so what remains IS a payment screen wearing a shop's chrome.
   *
   * Default FALSE. It changes where an ordinary storefront shopper lands after
   * buying a course, not just campaign traffic, so it is opt-in per tenant.
   *
   * NOT a second order-creation path: the same `doPlaceOrder` runs, with the
   * same house/merchant split, coupon handling, dropship attribution and
   * virtual/physical split-checkout. Only the chrome and the post-place
   * destination change. A parallel "instant" creation path would be a second
   * place for all of that to drift.
   */
  virtual_instant_pay: boolean;

  /**
   * When true, a member who registered through a CAMPAIGN / replicated page and
   * has a payable unpaid order is redirected to the payment page from every
   * route in the portal until that order is paid.
   *
   * Scoped to campaign signups on purpose. Someone who arrived through a
   * campaign came for one specific purchase and abandoning it mid-funnel is the
   * problem being solved; someone who joined through an ordinary referral link
   * (the QR one) is a member first and a buyer second, and locking them out of
   * their genealogy over an unpaid t-shirt would be indefensible. The
   * discriminator is `users.registered_via_page_id`, which is written at signup
   * ONLY when a campaign slug was carried in — a bare `/r/<code>` link has none.
   *
   * Orders awaiting an admin-set manual shipping fee never lock anyone: they sit
   * at `pending` with no pay button, so the lock would be inescapable.
   *
   * Default FALSE. Historically the reason for that was that an abandoned order
   * stayed locked until an ADMIN cancelled it — there was no member-facing
   * cancel route at all. There is one now (`member_order_cancel_enabled`), and
   * `order_auto_cancel_unpaid_days` clears the rest on a timer, so a tenant that
   * turns this on should turn one of those on too. It stays opt-in because the
   * lock itself is still a strong measure.
   */
  unpaid_order_lock_enabled: boolean;

  /**
   * When true, a member may cancel their OWN unpaid order (`pending`/`to_pay`)
   * from the portal. Default FALSE.
   *
   * Eligibility is NOT the status alone — see `orderCancelBlock` in
   * `order-cancel.ts`. An order whose payment proof is awaiting admin review, or
   * whose gateway payment is still live, can't be cancelled: the member has
   * already handed over money. That check is what keeps this from orphaning a
   * payment, and it's also why this ships off — a tenant opts in deliberately.
   *
   * Cancelling one order of a checkout GROUP cancels the whole group: the
   * gateway charges the group's summed total and settles whatever is still
   * payable at callback time, so a half-cancelled group over-charges the buyer.
   */
  member_order_cancel_enabled: boolean;

  /**
   * Number of days an UNPAID order (`pending`/`to_pay`, no proof submitted, no
   * live gateway payment) may sit before the worker cancels it. `null` or `0`
   * disables the sweep. Default `null` — deliberately unlike
   * `order_auto_complete_days`'s default of 7: auto-COMPLETE closes an order the
   * buyer already physically has, while auto-CANCEL destroys a live one, and a
   * destructive default arriving with an upgrade on a tenant that never asked
   * for it is indefensible.
   *
   * Age is measured from when the order last entered `pending`/`to_pay` per
   * `order_status_history` — NOT `orders.updated_at`, which unrelated admin
   * writes bump. A rejected proof therefore restarts the clock, so a member who
   * is actively re-submitting is never cut off mid-negotiation.
   */
  order_auto_cancel_unpaid_days: number | null;

  /**
   * When true, a fresh credentials LOGIN resumes an interrupted virtual
   * checkout: a payable unpaid virtual-only order sends the member straight to
   * its payment page, else a non-empty all-virtual (checked) cart sends them to
   * /checkout. One-shot at login only — NOT a lock like
   * `unpaid_order_lock_enabled`, so an activated member is never trapped; an
   * explicit `?next=` on the login URL always wins. Session restores on page
   * reload never trigger it. Default FALSE (opt-in per tenant, same rationale
   * as the rest of the virtual-funnel family).
   */
  virtual_checkout_resume_on_login: boolean;

  /**
   * Shows the Accounting section in the admin sidebar (/accounting) — the
   * sample-data preview pages plus the LIVE Accounting v1 surfaces (Payables,
   * Expenses, consignment reports, the Accounting report group). Flipped from
   * Settings → System (super-admin only, like every non-storefront feature key).
   */
  accounting_enabled: boolean;

  /**
   * Accounting v1 — consignment at the till ("Consignment Process sa POS").
   * Gates the cashier Consignment surfaces and every /pos/consignment* route
   * (server-enforced). Two-step true consignment: Consign-Out (CN-) moves
   * branch stock into a per-customer consigned holding; recording the
   * consignee's sell-through mints a Billing Statement (AR-) receivable;
   * payments (PAY-) apply per billing; returns (RTN-) restock the branch.
   * Consignment customers are members (users rows). Default off.
   */
  consignment_enabled: boolean;

  /**
   * Track 2A — stockist consignment ("sell-to-stockist" consignment). Gates
   * the branch-keyed SCN-/SAR-/SRTN-/SCPAY- module: consign-out moves house
   * stock into a stockist branch's inventory + consigned holdings; the
   * stockist's OWN POS sell-through auto-books the receivable at the frozen
   * consign-out price (kit codes / PV still go to the end buyer via the
   * ordinary POS path). Separate flag from `consignment_enabled` — the two
   * modules run independently. Default off.
   */
  stockist_consignment_enabled: boolean;

  /**
   * Cash/Bank register — require a funds account on money movements.
   *
   * When on, recording a supplier payment, an expense or a consignment receipt
   * must name the `funds_accounts` row the money moved through (400
   * `funds_account_required` otherwise), so every peso in or out is attributable
   * to a real till/bank account. Default OFF: historical rows carry no account,
   * and a tenant needs time to register theirs first. Enforced in the ROUTES,
   * and short-circuited while no active account exists — turning the flag on
   * with an empty register must never make posting impossible.
   */
  funds_account_required: boolean;

  /**
   * Accounting Phase 2 — house VAT. When on, `createOrderInTx` snapshots each
   * HOUSE line's VAT classification onto `order_items`
   * (`vat_enabled`/`vat_pct`/`tax_code` from the product's 3-code tax
   * registry). Prices are already VAT-INCLUSIVE — this never reprices
   * anything; it only records the decomposition inputs the billing documents
   * and the vat-sales report read. Merchant lines are untouched (their
   * `vat_enabled`/`vat_pct` is payout WITHHOLDING, a different mechanism).
   * Default off; flag-off checkout output is byte-identical.
   */
  house_vat_enabled: boolean;

  /**
   * Accounting Phase 2 — VAT rate snapshotted onto 'vatable' house lines while
   * `house_vat_enabled` is on. Money string (percent), `merchant_default_vat_pct`
   * precedent. PH standard rate: '12'.
   */
  house_vat_default_pct: string;

  /**
   * Phase 4 lean GL — the GL poster only books documents dated on/after this
   * date (YYYY-MM-DD, tenant timezone). NULL = no bound. The live-tenant
   * enable runbook sets it BEFORE flipping `accounting_enabled` so history
   * posts from a clean opening-balance line, not from the dawn of time.
   */
  accounting_posting_start_date: string | null;

  /**
   * Phase 4 lean GL — the month (1–12) the fiscal year starts. There is no
   * formal book close: the Balance Sheet derives Retained Earnings (cumulative
   * income − expense BEFORE the current fiscal year) and Net Income (current
   * fiscal year to the as-of date) from this boundary. 1 = calendar year, the
   * PH default.
   */
  accounting_fiscal_year_start_month: number;

  /**
   * R2 — general invoicing ("sell to anyone"). Gates the CREATION of general
   * (non-consignment) billing documents and the Receivables admin surfaces.
   *
   * Deliberately NOT a gate on the GL poster: the P34+ extractors run whenever
   * `accounting_enabled` is on, so a tenant that flips this off after posting
   * still gets its already-posted documents into the ledger. Gating the poster
   * instead would strand posted AR against a register row that never clears.
   * Default off.
   */
  ar_invoicing_enabled: boolean;

  /**
   * R2 — the `payment_terms.code` used when neither the document nor the
   * customer names one. `due_on_receipt` is the safe default: a term nobody
   * chose should not silently extend credit.
   */
  ar_default_terms_code: string;

  /**
   * R2 — what happens when posting a billing would push a customer past their
   * `credit_limit`. `off` = never checked; `warn` = post, returning
   * `warnings:['credit_limit_exceeded']`; `block` = 409. Evaluated at POST
   * only (never at draft), under the customer advisory lock, so two concurrent
   * posts serialize and exactly one passes when only one fits.
   */
  ar_credit_limit_mode: 'off' | 'warn' | 'block';

  /**
   * Which product this deployment presents itself as. `mlm` (default) is the
   * full platform; `accounting` is the books-only edition, which drives the
   * admin sidebar (hiding Network/Compensation/Payouts/Genealogy and friends)
   * and nothing else — every capability behind it is still its own flag, so
   * this is a presentation switch, never an authorization one.
   */
  platform_edition: 'mlm' | 'accounting';

  /**
   * Membership tier whose per-product ABSOLUTE prices apply to a buyer with NO
   * active slot ("non-activated member"), so a prospect can enjoy a member
   * price before they activate. `null` (default) = slotless buyers pay SRP,
   * which is the historical behaviour.
   *
   * The predicate is deliberately "the buyer has no ACTIVE slot at all", NOT
   * "no slot was supplied for pricing" — a cashier who deselects an activated
   * member's slot at the till must not unlock this discount. Rank pricing never
   * applies (no slot ⇒ no rank) and PV is never affected. Dropship orders are
   * exempt (the buyer pays SRP by design so the sharer's margin holds).
   *
   * Validated against `memberships.id` when saved; a tier archived afterwards
   * simply stops matching and the buyer falls back to SRP.
   */
  non_activated_pricing_membership_id: string | null;

  /**
   * Which slot's per-product ABSOLUTE price a member with MULTIPLE slots pays.
   *
   * - `'active_slot'` (default) — the tier/rank of the slot they are currently
   *   logged in on. Switching slots re-prices the catalog.
   * - `'best_across_slots'` — the lowest price any of their ACTIVE slots
   *   qualifies for, regardless of which one is selected.
   *
   * PRICING ONLY. `orders.buyer_slot_id` — and therefore PV, commissions,
   * binary points, the CD garnish and the wallet debit — always follows the
   * SELECTED slot in both modes. Pricing slot and attribution slot are separate
   * concepts; see `resolveBuyerSegmentKeys`.
   *
   * Stockist / branch prices are NEVER unioned across slots: `branch_pricing`
   * is a BRANCH entitlement, so unioning would hand a member stockist pricing
   * at the till on an ordinary retail sale merely because they own a stockist
   * branch on some other slot. Non-activated ("prospect") pricing is likewise
   * unreachable in best mode — it requires zero active slots by definition.
   */
  pricing_slot_source: PricingSlotSource;

  /**
   * BF4: display name for the coin currency (e.g. "Gems", "Reward Points").
   * Surfaced everywhere coins appear in the SPAs. Default "Coins".
   */
  coin_label: string;

  /**
   * BF4: per-plan coin reward, keyed by `plan_code` (parallel to `daily_caps`).
   * When a plan pays a CASH credit, the framework rides a flat coin credit of
   * `coin_rewards[plan_code]` onto each earner slot (`currency_kind='coin'`,
   * kind `coin_reward`) — "coins alongside money" for any plan, no per-plan
   * code. Missing key / `<= 0` = that plan pays no coins. Free slots are
   * filtered out as earners (BF2), so they receive no coin reward either.
   */
  coin_rewards: Record<string, number>;
  /**
   * Flat AI-Credit rider per plan, mirroring `coin_rewards`: every CASH earner
   * of `plan_code` also receives this many assistant answers. Missing / <= 0 =
   * no rider. Credits aren't cash, so daily/lifetime caps and the CD garnish
   * don't apply to them.
   */
  ai_credit_rewards: Record<string, number>;

  /**
   * Admin-friendly display names for ledger `kind` values, keyed by the raw
   * kind (e.g. `{ direct: 'Direct Referral' }`). Sparse — only the entries an
   * admin renamed. Built-in defaults for every known kind live in
   * `LEDGER_KIND_LABEL_DEFAULTS` (`ledger-labels.ts`); `labelizeLedgerKind`
   * resolves override → default → humanized fallback. Shown in member Earnings
   * + admin wallet/ledger views.
   */
  ledger_labels: Record<string, string>;

  /**
   * Admin-friendly display names for ledger `source_plan` values, same shape +
   * resolution as `ledger_labels` (defaults in `LEDGER_SOURCE_PLAN_LABEL_DEFAULTS`).
   */
  ledger_source_plan_labels: Record<string, string>;

  /**
   * JM-EX9: admin-authored member notification templates. The materializer
   * uses these when fanning out an in-app notification per realized ledger
   * credit. Today only `earnings` is wired; future kinds (signup, payout)
   * extend this map.
   *
   * Placeholders supported in title/body (Mustache-ish `{name}`):
   *   {amount}        — formatted credit amount
   *   {currency}      — Cash / GC / Coin / Token
   *   {source_plan}   — humanized via labelizeSourcePlan
   *   {kind}          — humanized via labelizeLedgerKind
   *   {trigger_user}  — buyer/redeemer display name when resolvable, else "—"
   */
  notification_templates: NotificationTemplatesConfig;

  /**
   * Email alert to ADMINS when a member submits manual-payment proof on
   * ecommerce (the "payment waiting for review" event — proof submission only,
   * not approval, not gateway webhooks). Fired post-commit, fire-and-forget;
   * requires the deployment's SMTP env vars (SMTP_HOST etc.) to be set or the
   * send silently no-ops with a logged warning. `recipients` empty ⇒ falls back
   * to every super_admin user's account email. Default disabled.
   */
  manual_payment_admin_email: ManualPaymentAdminEmailConfig;

  /**
   * Merchant feature (M1): default platform fee % charged on every merchant
   * product sale. Applied when a merchant adds a product (admin can override
   * per product at approval) and SNAPSHOTTED onto `products.platform_fee_pct`,
   * so changing this never rewrites past sales. The merchant earns
   * `lineTotal × (1 − pct/100)`; the platform keeps the rest. Money string.
   */
  platform_fee_pct: string;

  /**
   * Merchant feature: the inclusive `[min, max]` band a MERCHANT-declared
   * platform fee % must fall in. The merchant sets their own fee per product;
   * the server clamps it to this range (`clampFeeToRange`) on submit and admin
   * approval. `platform_fee_pct` (the default) should sit inside the band.
   * Money strings (0–100).
   */
  platform_fee_min_pct: string;
  platform_fee_max_pct: string;

  /**
   * Merchant pricing model: default PV-commission % the admin sees pre-filled at
   * product review. PV for a merchant product = `pv_commission_pct% × commission`
   * (commission = price × platform_fee_pct%). Per-product overridable. Money string.
   */
  merchant_pv_commission_pct: string;

  /**
   * Merchant pricing model: default VAT % applied to the merchant's net credit
   * when VAT is enabled on a product (`net = gross × (1 − vat_pct/100)`). Money string.
   */
  merchant_default_vat_pct: string;

  /**
   * Realtime Unilevel (RTU) defaults pre-filled in the merchant-product approval
   * dialog's RTU tab. The pool = `merchant_rtu_ceiling_pct% × commission`, split
   * across sponsor levels by `merchant_rtu_default_shares` — each entry is that
   * level's % OF THE POOL (index 0 = buyer L0). Level count = array length. The
   * admin can override per product; the resolved per-level shares are stored on
   * the product. Money strings (each 0–100).
   */
  merchant_rtu_ceiling_pct: string;
  merchant_rtu_default_shares: string[];

  /**
   * Merchant pricing model: default state of the per-product VAT toggle the
   * admin sees at product review. Per-product overridable.
   */
  merchant_vat_enabled_default: boolean;

  /**
   * Merchant feature (M1): when true, a completed merchant sub-order ALSO fires
   * the `order_completed` comp event (PV/points/commissions to uplines) like a
   * house order. Default FALSE = marketplace-only: merchant sales carry no PV
   * and fire no comp; they only credit the merchant wallet.
   */
  merchant_sales_earn_comp: boolean;

  /**
   * Order-by-Area: when true, an ecom "ship" order's fulfilling branch
   * (`ship_from_branch_id`) is AUTO-ROUTED from the buyer's shipping region to
   * the ecommerce-ready branch/stockist tagged with that courier zone (falling
   * back to the designated Main branch), replacing the member's manual branch
   * pick. Default FALSE = legacy behavior (member picks / admin assigns).
   */
  order_by_area: boolean;

  /**
   * Order-by-Area: when true, a stockist that FULFILLS a house ecom order
   * accrues a receivable (Main owes the stockist the full retail sale value) on
   * the stockist ledger, reversed on refund/cancel. Default FALSE.
   */
  stockist_receivables: boolean;

  /**
   * Merchant feature (M1): admin-defined registration requirements shown on the
   * public merchant registration form (file uploads / text fields). Empty by
   * default — the admin configures them in Settings. See `merchant.ts`.
   */
  merchant_requirements: MerchantRequirementDef[];

  /**
   * Merchant feature (M1): payout method registry + deductions for MERCHANT
   * cash-outs, independent of the member `payout_deductions` registry (merchants
   * use different methods/schedules). Same shape + math.
   */
  merchant_payout_deductions: PayoutDeductionsConfig;

  /**
   * Merchant feature (M1): cadence the admin batches merchant payouts on
   * (informational; the batch flow is manual). Default monthly.
   */
  merchant_payout_schedule: MerchantPayoutSchedule;

  /**
   * LMS Content Creators — the merchant split applied to courses.
   *
   * `creator_platform_fee_pct`: default platform fee % on every creator course
   * sale (money string). Per-creator override on `creators.platform_fee_pct`,
   * clamped into `[creator_platform_fee_min_pct, creator_platform_fee_max_pct]`
   * and SNAPSHOTTED onto `order_items.platform_fee_pct` at checkout — a later
   * change never rewrites a past sale. The creator earns
   * `lineTotal × (1 − pct/100)`; the platform keeps the rest.
   */
  creator_platform_fee_pct: string;
  creator_platform_fee_min_pct: string;
  creator_platform_fee_max_pct: string;

  /**
   * LMS: when true, a completed CREATOR course sub-order ALSO fires the
   * `order_completed` comp event like a house order. Default FALSE = creator
   * courses are marketplace-only: no PV, no comp — they credit the creator
   * wallet only. House courses (no creator) fire comp regardless.
   */
  creator_sales_earn_comp: boolean;

  /**
   * LMS: admin-defined creator registration requirements (same shape as the
   * merchant ones). Empty by default.
   */
  creator_requirements: MerchantRequirementDef[];

  /**
   * LMS: payout method registry + deductions for CREATOR cash-outs — its own
   * registry (rates), independent of the member and merchant ones. The
   * deduction ORDER is the member registry's (one tenant accounting policy).
   */
  creator_payout_deductions: PayoutDeductionsConfig;

  /**
   * Storefront merchandising: the home "Flash Sale" strip. `productIds` are the
   * products to feature, `endsAt` an optional ISO countdown target (null = no
   * countdown), `enabled` the on/off. The public storefront reads the resolved
   * product rows via `GET /storefront/flash-sale`; when disabled or empty the
   * strip is hidden. Admin-managed in the Flash Sale tab.
   */
  flash_sale: FlashSaleConfig;

  /**
   * Storefront landing "packages" — an admin-curated, ORDERED list of product ids
   * surfaced in a tenant landing's packages section (e.g. mhyel's bespoke landing).
   * Resolved to live product rows via `GET /storefront/landing-packages` so the
   * cards are real, cart-able products. Empty = the section is hidden / falls back.
   * Admin-managed in the Landing Packages tab.
   */
  landing_packages: LandingPackagesConfig;

  /**
   * Storefront "Featured Packages" — an admin-curated, ORDERED list of product
   * ids headlined in a placeable `featuredPackages` storefront section. Resolved
   * to live, cart-able product rows via `GET /storefront/featured-packages` so
   * the cards are real products. `enabled` is the on/off, `heading` an optional
   * section-title override. Empty or disabled = the section is hidden.
   * Admin-managed in the Featured Packages tab.
   */
  featured_packages: FeaturedPackagesConfig;

  /**
   * Storefront CMS content: hero slides, promo/campaign banners, vouchers,
   * collections, store-stat blocks, trust/assurance badges, header links +
   * search suggestions, footer columns + brand copy, and a few inline copy
   * strings. Admin-managed in the Storefront section; served to the public
   * buyer storefront via `GET /storefront/content`. See `storefront-content.ts`.
   */
  storefront: StorefrontConfig;

  /**
   * Chat (M-chat): master on/off for buyer↔seller messaging. When false
   * (default, opt-in) the "Message seller" button + chat bells are hidden and
   * the `/me/conversations`, `/admin/conversations`, `/merchant/conversations`
   * routes 403 — so a tenant that doesn't use chat carries zero polling load.
   */
  messaging_enabled: boolean;

  /**
   * Auto chat-block on spam. When ON, a member who sends at least
   * `chat_spam_threshold` messages within the trailing `chat_spam_window_seconds`
   * is automatically chat-blocked (`users.chat_blocked = true`, audited with
   * `reason: 'auto_spam'`) — admin/merchant replies are unaffected and an admin
   * can unblock from the Messages inbox. OFF by default (opt-in); the defaults
   * sit ABOVE the per-minute send rate-limit so only sustained flooding trips it.
   */
  chat_spam_autoblock_enabled: boolean;
  /** Trailing window (seconds) the auto-block counts a member's messages over. */
  chat_spam_window_seconds: number;
  /** Member message count within the window that trips the auto chat-block. */
  chat_spam_threshold: number;

  /**
   * Auto-Ship: master on/off for the member monthly auto-ship subscriptions.
   * ON by default (the feature shipped always-enabled). When false the member
   * Auto-Ship nav item is hidden, `POST/PATCH /me/autoship` 403, and the worker
   * engine (`processDueAutoships`) skips all due subscriptions — existing rows
   * are preserved and resume when re-enabled.
   */
  autoship_enabled: boolean;

  /**
   * Member monthly subscription (Phase C): master switch for the paid
   * subscription that unlocks the admin-configured "special links" (Zoom/Vimeo
   * live). Off = the subscribe page + special-links surface are hidden and the
   * wallet renewal engine is a no-op (existing access periods still run out).
   * Default off.
   */
  member_subscription_enabled: boolean;
  /**
   * Member subscription plan config: the monthly `price` (money string) and the
   * wallet `currency` charged ('cash' | 'gc'). Read by the subscribe page + the
   * wallet renewal engine.
   */
  member_subscription: { price: string; currency: 'cash' | 'gc' };

  /**
   * AI Assistant (Phase 1): master switch for the member-facing /assistant
   * page + API. Off = nav item hidden, assistant routes 403, and the member
   * page (if deep-linked) shows only the scripted help fallback. Default OFF.
   * Enabling also requires the server-side ANTHROPIC_API_KEY — without it the
   * API degrades with `assistant_not_configured` and the UI falls back.
   */
  assistant_enabled: boolean;
  /**
   * AI Assistant config (operator-private — never exposed through
   * `GET /tenant/config`; only the boolean flag above is public). See
   * `AssistantConfig` for per-key docs.
   */
  assistant: AssistantConfig;

  /**
   * Miracles Protocol coaching MASTER SWITCH. Off (default) = the coaching
   * product does not exist for this tenant: every `/coaching/*` route 404s, the
   * coach SPA refuses to load, and the admin Coaching card is hidden from
   * anyone who cannot turn it on. Only the tenant that sells the coach app
   * (SuccessMall → Miracles Protocol) turns it on.
   */
  coaching_enabled: boolean;
  /**
   * Coaching config (operator-private, never exposed through
   * `GET /tenant/config`; only the boolean flag above is public). See
   * `CoachingConfig` for per-key docs.
   */
  coaching: CoachingConfig;
};

/**
 * Claude models a tenant may select for the assistant. Haiku is the cost
 * default; Sonnet/Opus are per-tenant upgrades (roughly 3× / 5× input cost).
 * The API validates against this allowlist and silently falls back to the
 * default on an unknown id.
 */
export const ASSISTANT_MODELS = ['claude-haiku-4-5', 'claude-sonnet-5', 'claude-opus-5'] as const;
export type AssistantModel = (typeof ASSISTANT_MODELS)[number];

export type AssistantConfig = {
  /** Claude model id from `ASSISTANT_MODELS`. */
  model: AssistantModel;
  /** Assistant messages per member per tenant-day; `<= 0` = unlimited. */
  daily_cap: number;
  /**
   * Monthly spend ceiling in USD (money string) across the whole tenant,
   * compared against the summed per-exchange cost estimates. `''`/`'0'` =
   * unlimited. When exhausted, members get the scripted fallback until the
   * next month.
   */
  monthly_budget_usd: string;
  /** Allow the server-side web-search tool (~$0.01 per search, budget-capped). */
  web_search_enabled: boolean;
  /**
   * Cross-chat recall: let the assistant carry context between a member's own
   * conversations. Turns on BOTH layers — the background digest of idle threads
   * (folded into later chats' seed) and the `search_past_chats` /
   * `read_past_chat` tools.
   *
   * Off by default: it adds a small background spend per idle thread and is a
   * privacy posture a tenant should choose rather than inherit. A member can
   * still opt out individually (`users.assistant_recall_opt_out`) — recall
   * requires BOTH.
   */
  recall_enabled: boolean;
  /**
   * How long a thread must sit untouched before it's eligible for a digest.
   * Long enough that a member on a lunch break doesn't get their live thread
   * summarised mid-conversation.
   */
  recall_idle_minutes: number;
  /**
   * Only digest threads whose last message is newer than this. This is the
   * anti-stampede guard: without it, the first sweep after `recall_enabled`
   * flips on would make EVERY historical thread eligible at once and burn the
   * month's budget in a single tick.
   */
  recall_digest_max_age_days: number;
  /**
   * Persona overrides merged over `DEFAULT_PERSONAS` by id (see
   * `assistant/personas.ts`), edited in Settings → AI Assistant.
   */
  personas: AssistantPersonaOverride[];
  /**
   * Meter assistant usage with AI Credits: each member gets a free monthly
   * allowance, and beyond that every answer spends one credit from their
   * wallet (bought via top-up, earned from the comp plan or product rewards).
   *
   * Off by default — with it off the daily cap and the tenant monthly budget
   * are the only limits, exactly as before. Those two stay in force either
   * way: the budget is the house's catastrophe backstop and blocks everyone,
   * credit-holders included.
   */
  credits_enabled: boolean;
  /** Free answers per member per tenant-month. 0 = none (every answer costs). */
  credits_monthly_free: number;
  /**
   * Peso price of one credit, as a money string. Empty = not for sale (the
   * member-facing buy flow is hidden; credits can still be earned/granted).
   */
  credits_price: string;
  /**
   * Land members on /assistant after login instead of the dashboard, for
   * tenants whose assistant IS the product. Only honored while
   * `assistant_enabled` is on; an explicit `?next=` and the checkout resume
   * still win over it.
   */
  home_promotion: boolean;
  /**
   * Tenant-authored knowledge text folded into the assistant's system prompt
   * alongside the storefront FAQ (company story, product notes, policies…).
   */
  knowledge: string;
};

export const ASSISTANT_CONFIG_DEFAULT: AssistantConfig = {
  model: 'claude-haiku-4-5',
  daily_cap: 30,
  monthly_budget_usd: '',
  web_search_enabled: true,
  recall_enabled: false,
  recall_idle_minutes: 120,
  recall_digest_max_age_days: 30,
  personas: [],
  credits_enabled: false,
  credits_monthly_free: 30,
  credits_price: '',
  home_promotion: false,
  knowledge: '',
};

/**
 * Where natal charts come from. `local` is the on-box ephemeris
 * (astronomy-engine, Lahiri, whole-sign): free, no key, and the fallback for
 * every other provider. `prokerala` is the Prokerala v2 API (the engine John
 * Calub chose on 2026-09-09), which falls back to `local` on any failure.
 */
export const COACHING_ASTRO_PROVIDERS = ['local', 'prokerala'] as const;
export type CoachingAstroProvider = (typeof COACHING_ASTRO_PROVIDERS)[number];

export type CoachingConfig = {
  /** Natal chart engine; see `COACHING_ASTRO_PROVIDERS`. */
  astro_provider: CoachingAstroProvider;
  /**
   * How long a Prokerala natal chart may be kept before it is fetched again.
   * `null` (default) = keep until the member's birth details change, so a chart
   * costs credits once; John Calub's team cleared storing results with
   * Prokerala (2026-09-15). A number re-fetches after that many hours, e.g. 24
   * for Prokerala's standard caching window. Local charts are always kept.
   */
  astro_natal_cache_hours: number | null;
  /** Alert admins when this percent of the monthly Prokerala credit pack is used. */
  astro_credit_alert_pct: number;
  /** Master Kala messages per tenant-day for a free member; `<= 0` = unlimited. */
  free_daily_messages: number;
  /** Master Kala messages per tenant-day for a Pro member; `<= 0` = unlimited. */
  pro_daily_messages: number;
  /** Active goals a free member may hold at once; `<= 0` = unlimited. */
  free_active_goals: number;
  /** Pro members get one streak freeze per month. */
  streak_freeze_pro: boolean;
  /**
   * Gate the natal chart and the daily Vedic guidance behind Pro. Default off:
   * during the beta every member sees them, and the tenant decides later
   * whether they belong to the paid tier (blueprint's tier table says Pro).
   */
  chart_guidance_pro_only: boolean;
  /**
   * Gate the Align tools (breathwork, frequencies, biorhythm) and the WRITTEN
   * weekly review summary behind Pro. Default off, the beta stance: every
   * member uses them. On, a free member sees an upgrade prompt on the three
   * tools and closes their week with the built-in summary, silently, the way
   * the evening review already works. The 369 practice and the vision board
   * stay free either way.
   */
  tools_pro_only: boolean;
  /**
   * The astrology provider's monthly credit pack, i.e. the DENOMINATOR
   * `astro_credit_alert_pct` is a percent of. Prokerala's free tier is 5,000.
   * `0` = no pack to measure against, so the Coaching usage card shows calls
   * only. Nothing enforces it: the provider does.
   */
  astro_monthly_credits: number;
  /** Claude model for coaching generation (blueprint, daily engine, guidance line). */
  model: AssistantModel;
  /**
   * Write each member's daily payload (priority action, affirmation, insight)
   * with Claude. Off = deterministic templates only, which cost nothing.
   */
  daily_engine_enabled: boolean;
  /**
   * Coaching's OWN monthly Claude budget in USD: Master Kala's turns, digests of
   * Kala threads and every generated coaching text. Separate from
   * `assistant.monthly_budget_usd`, which now counts only the store assistant,
   * so neither can use up the other. `''` (default) = unlimited. When it runs
   * out Master Kala pauses until next month and generated text falls back to
   * the built-in wording, which generation starts doing at 90% to leave room
   * for member turns.
   */
  monthly_budget_usd: string;
  /**
   * The tenant's member community (a Telegram, Facebook, Viber or Discord
   * link), shown as a card on the coach app's Home and Profile. `''` (default)
   * = no community, and the card is not rendered. https only; a phone hands
   * it to its own browser, which is where those apps live.
   */
  community_url: string;
  /** The card's title; `''` = "Join the community". */
  community_label: string;
  /**
   * Which coaching features belong to which plan, edited in Admin -> Coaching
   * -> Features. This SUPERSEDES `chart_guidance_pro_only`, `tools_pro_only`,
   * `streak_freeze_pro`, `free_daily_messages`, `pro_daily_messages` and
   * `free_active_goals`, which stay as the fallback: `resolveCoachingPlanFeatures`
   * reads this key by key and drops back to those switches for anything the
   * tenant has not set, so a tenant that never opens the editor behaves exactly
   * as it did. `null` (default) = nothing saved, every key falls back.
   */
  plan_features: CoachingPlanFeatures | null;
  /**
   * Subscription required to use the app (Miracles Protocol, 2026-10-13: no
   * free tier). On, a member with no live Pro-or-higher window gets 402
   * `coaching_subscription_required` on every coaching route except the few
   * that finish onboarding and pay, and the coach app shows the paywall as a
   * door. Off (default): the free tier behaves exactly as before.
   */
  paid_only: boolean;
  /**
   * AI Credits each coaching action costs, by action key (`COACHING_CREDIT_ACTIONS`
   * in `coaching/credits.ts`). A missing key means the default (1), 0 makes
   * that action free. Edited as a typed table on Admin -> Coaching -> Credits.
   * Nothing is exempt: Master Kala's turns and the automatic daily generations
   * alike spend credits; a member at zero gets each generator's template.
   */
  credit_costs: CoachingCreditCosts;
  /** Pesos per US dollar for the admin cost figures (the API meters in USD). */
  usd_php_rate: number;
  /**
   * What the two paid tiers are CALLED to members and staff (Miracles
   * Protocol, 2026-10-13: "Standard" and "Premium"). The keys `pro` / `elite`
   * stay the stable identifiers in every table and route; only the words
   * change. Read through `coachingTierLabels`, never `COACHING_TIER_LABELS`
   * directly, which is only the default.
   */
  tier_labels: CoachingTierLabels;
  /**
   * The listening intentions a frequency track is filed under, in the admin's
   * order (Miracles Protocol, 2026-10-13: Mantra, Affirmation and whatever
   * else they add). `[]` (default) = the seven built-in categories. `hue`
   * (0-360) colours the tile and the player; null = one derived from the key.
   * Read through `coachingFrequencyCategories`.
   */
  frequency_categories: CoachingFrequencyCategoryConfig[];
  /**
   * The content library's categories, in the admin's order; `[]` (default) =
   * no categories, the library is one list. Read through `coachingLibraryCategories`.
   */
  library_categories: CoachingLibraryCategoryConfig[];
};

export type CoachingTierLabels = { pro: string; elite: string };
export type CoachingFrequencyCategoryConfig = { key: string; label: string; hue: number | null };
export type CoachingLibraryCategoryConfig = { key: string; label: string };

export const COACHING_CONFIG_DEFAULT: CoachingConfig = {
  astro_provider: 'local',
  astro_natal_cache_hours: null,
  astro_credit_alert_pct: 80,
  free_daily_messages: 5,
  pro_daily_messages: 30,
  free_active_goals: 1,
  streak_freeze_pro: true,
  chart_guidance_pro_only: false,
  tools_pro_only: false,
  astro_monthly_credits: 5000,
  model: 'claude-haiku-4-5',
  daily_engine_enabled: true,
  monthly_budget_usd: '',
  community_url: '',
  community_label: '',
  plan_features: null,
  paid_only: false,
  credit_costs: {},
  usd_php_rate: 57,
  tier_labels: { pro: 'Success Pro', elite: 'Success Elite' },
  frequency_categories: [],
  library_categories: [],
};

export type FlashSaleConfig = {
  enabled: boolean;
  productIds: string[];
  endsAt: string | null;
};

export type LandingPackagesConfig = {
  /** Ordered product ids; the landing maps them to its package cards in order. */
  productIds: string[];
};

export type FeaturedPackagesConfig = {
  /** On/off for the Featured Packages storefront section. */
  enabled: boolean;
  /** Ordered product ids; the section renders them as cards in this order. */
  productIds: string[];
  /** Optional section-heading override (null = the section's built-in default). */
  heading: string | null;
};

export type NotificationEventTemplate = {
  enabled: boolean;
  title: string;
  body: string;
};

export type NotificationTemplatesConfig = {
  earnings: NotificationEventTemplate;
  /**
   * Per-event member notification copy, keyed by a stable event code (see
   * `NOTIFICATION_EVENT_CODES`). The API renders these when it fires the
   * matching event (order placed, payment submitted, cash-out, …). Placeholders:
   *   {order_code} {amount} {currency} {method} {reason} {product}
   * `rank_up` adds: {rank} {previous_rank} {slot_code}
   * Gift check events add: {label} (the tenant's name for a gift check)
   *   {name} (the program) {code} {expires} (a date) {validity} ("Valid until
   *   Oct 30, 2026." or "It does not expire."); `voucher_redeemed` also {order_code};
   *   `birthday_voucher_available` has {label} {name} (the birthday programs)
   *   and {until} ("today" or "by Oct 9, 2026")
   * LMS events add:
   *   {course_title} {lesson_title} {test_title} {score} {pass_mark} {days}
   *   {replier} {learner_name} {order_code} {announcement_title}
   * Disabled events fire no notification. Optional on the wire (a partial PATCH
   * may omit it); `resolveFeatures` always fills it from defaults.
   */
  events?: Record<string, NotificationEventTemplate>;
};

/** The core (non-LMS) member-facing notification events with admin-editable copy. */
export const CORE_NOTIFICATION_EVENT_CODES = [
  'order_placed',
  'order_pending_payment',
  // Manual-fee courier: the order is placed but unpayable until an admin sets
  // the shipping fee, then again once they have.
  'order_awaiting_shipping_fee',
  'order_shipping_fee_set',
  'payment_submitted',
  'dropship_sale',
  'cashout_requested',
  'cashout_rejected',
  'cashout_paid',
  'topup_submitted',
  'topup_approved',
  'topup_rejected',
  'autoship_completed',
  'autoship_skipped',
  // Stairstep: a slot's CURRENT rank was raised (live rank-up, earnings
  // threshold, period close or a distribution run).
  'rank_up',
  // Gift checks (cashier phase 4): one was issued (earned or by hand), one was
  // used at the till, one expires within three days.
  'voucher_issued',
  'voucher_redeemed',
  'voucher_expiring',
  // Phase 5: a birthday gift check is ready to claim (once per birthday).
  'birthday_voucher_available',
  // The till's receipt notice to the customer (every till sale but a stockist
  // transfer). Its template ships OFF; see NOTIFICATION_TEMPLATES_DEFAULT.
  'pos_sale_completed',
] as const;

/**
 * LMS Phase 2 — learner-facing course events. Only meaningful while
 * `lms_enabled`; the admin editor renders them in their own gated group so a
 * tenant admin on a non-LMS tenant never learns the feature exists.
 */
export const LMS_LEARNER_NOTIFICATION_EVENT_CODES = [
  'course_enrolled',
  'question_answered',
  'test_passed',
  'test_failed',
  'certificate_issued',
  'learning_reminder',
  'course_announcement',
  // Creator-model subscriptions: an automatic renewal went through or did not,
  // and a window with no auto-renewal ends in three days.
  'subscription_renewed',
  'subscription_renewal_failed',
  'subscription_expiring',
  // A Stripe card subscription billed the next period (the wallet bell above
  // says "paid from your wallet").
  'subscription_card_renewed',
] as const;

/** LMS Phase 2 — content-creator-facing events (delivered to the creator SPA bell). */
export const LMS_CREATOR_NOTIFICATION_EVENT_CODES = [
  'creator_approved',
  'creator_rejected',
  'creator_suspended',
  'course_approved',
  'course_rejected',
  // Phase 8 — the CONTENT review decision, distinct from the listing approval
  // above: a creator can hold an approved listing and a rejected re-upload.
  'course_content_approved',
  'course_content_rejected',
  // Phase 10 — the admin's decision on a creator's SUBSCRIPTION plan, which is
  // a product decision like a course listing but has no course to point at.
  'subscription_plan_approved',
  'subscription_plan_rejected',
  'creator_sale',
  'creator_payout_paid',
  'creator_payout_rejected',
  'creator_question',
  'creator_grading',
] as const;

export const LMS_NOTIFICATION_EVENT_CODES = [
  ...LMS_LEARNER_NOTIFICATION_EVENT_CODES,
  ...LMS_CREATOR_NOTIFICATION_EVENT_CODES,
] as const;

/**
 * Owner/staff alerts carry HARDCODED copy (the kyc / feedback / cash-out
 * convention) — they are NOT admin-templated, so they never appear in
 * `NOTIFICATION_EVENT_CODES`. Exported so the shared icon map and the tests
 * agree on the literals.
 */
export const LMS_OWNER_ALERT_KINDS = [
  'creator_applied',
  'creator_course_submitted',
  'creator_payout_requested',
  'house_grading_pending',
  // Phase 8 — playable content changed on an already-approved course.
  'creator_course_content_changed',
  // Phase 8 — a member reported content (fires on the FIRST open report per
  // target only; a pile-on must not be a pager storm).
  'content_reported',
  // Phase 10 — a third-party creator submitted or re-priced a subscription
  // plan, so a product nobody has approved is waiting to go on sale.
  'creator_subscription_plan_submitted',
] as const;

/** The member-facing notification events with admin-editable copy. */
export const NOTIFICATION_EVENT_CODES = [
  ...CORE_NOTIFICATION_EVENT_CODES,
  ...LMS_NOTIFICATION_EVENT_CODES,
] as const;

export type NotificationEventCode = (typeof NOTIFICATION_EVENT_CODES)[number];
export type LmsNotificationEventCode = (typeof LMS_NOTIFICATION_EVENT_CODES)[number];
export type LmsOwnerAlertKind = (typeof LMS_OWNER_ALERT_KINDS)[number];

export const NOTIFICATION_TEMPLATES_DEFAULT: NotificationTemplatesConfig = {
  earnings: {
    enabled: true,
    title: 'You earned {amount} {currency}',
    body: 'From {source_plan} triggered by {trigger_user}',
  },
  events: {
    order_placed: {
      enabled: true,
      title: 'Order placed',
      body: 'We received your order {order_code}.',
    },
    order_pending_payment: {
      enabled: true,
      title: 'Payment needed for {order_code}',
      body: 'Upload your payment proof to start processing your order.',
    },
    order_awaiting_shipping_fee: {
      enabled: true,
      title: 'We’re computing your shipping fee for {order_code}',
      body: 'Our team will contact you to confirm the delivery details. Once we set the shipping fee you’ll be able to pay for this order.',
    },
    order_shipping_fee_set: {
      enabled: true,
      title: 'Your order {order_code} is ready for payment',
      body: 'We’ve set the shipping fee for your order. Open it to see the new total and pay.',
    },
    payment_submitted: {
      enabled: true,
      title: 'Payment proof received',
      body: "We're reviewing your payment for order {order_code}.",
    },
    dropship_sale: {
      enabled: true,
      title: 'You made a sale!',
      body: 'Someone purchased {product} through your link — order {order_code}.',
    },
    cashout_requested: {
      enabled: true,
      title: 'Cash-out requested',
      body: 'Your cash-out of {amount} {currency} is pending review.',
    },
    cashout_rejected: {
      enabled: true,
      title: 'Cash-out rejected',
      body: 'Your cash-out was rejected: {reason}. The funds were returned to your wallet.',
    },
    cashout_paid: {
      enabled: true,
      title: 'Cash-out paid',
      body: 'Your cash-out of {amount} {currency} has been paid.',
    },
    topup_submitted: {
      enabled: true,
      title: 'Top-up proof received',
      body: "We're reviewing your wallet top-up of {amount} {currency}.",
    },
    topup_approved: {
      enabled: true,
      title: 'Wallet top-up approved',
      body: 'Your wallet was credited {amount} {currency}.',
    },
    topup_rejected: {
      enabled: true,
      title: 'Wallet top-up rejected',
      body: 'Your top-up was rejected: {reason}.',
    },
    autoship_completed: {
      enabled: true,
      title: 'Auto-ship order placed',
      body: 'Your auto-ship order {order_code} was placed and paid from your wallet.',
    },
    autoship_skipped: {
      enabled: true,
      title: 'Auto-ship skipped',
      body: "We couldn't run your auto-ship — your {currency} wallet was short. Top up and we'll try again next cycle.",
    },
    rank_up: {
      enabled: true,
      title: 'You reached {rank}!',
      body: 'Congratulations! Slot {slot_code} ranked up from {previous_rank} to {rank}.',
    },
    voucher_issued: {
      enabled: true,
      // Neutral on purpose: the same notice goes out for one issued by hand.
      title: 'You have a new {label}',
      body: '{name}. Show code {code} at the counter to use it. {validity}',
    },
    voucher_redeemed: {
      enabled: true,
      title: '{label} used',
      body: '{name} ({code}) was used on order {order_code}.',
    },
    voucher_expiring: {
      enabled: true,
      title: 'Your {label} expires soon',
      body: '{name} ({code}) expires on {expires}. Use it before then.',
    },
    birthday_voucher_available: {
      enabled: true,
      title: 'Happy birthday!',
      body: 'A birthday {label} is waiting for you: {name}. Claim it {until} on your Gift Checks page.',
    },
    // OFF by default: switched on, every till sale drops a notice in the
    // buyer's bell, which a busy live till must opt in to, not inherit.
    pos_sale_completed: {
      enabled: false,
      title: 'Thanks for your purchase',
      body: 'Order {order_code} at {branch}: {total}. Tap to see your receipt.',
    },
    // ── LMS Phase 2: learner events ──
    course_enrolled: {
      enabled: true,
      title: 'You\u2019re enrolled in {course_title}',
      body: 'Your lessons are ready whenever you are \u2014 open the course to start.',
    },
    question_answered: {
      enabled: true,
      title: 'New reply to your question',
      body: '{replier} replied in {lesson_title} ({course_title}). Open the lesson to read it.',
    },
    test_passed: {
      enabled: true,
      title: 'You passed {test_title}',
      body: 'Nice work \u2014 you scored {score}% in {course_title}.',
    },
    test_failed: {
      enabled: true,
      title: 'Your {test_title} results are in',
      body: 'You scored {score}% in {course_title}; the pass mark is {pass_mark}%. Review the lessons and try again.',
    },
    certificate_issued: {
      enabled: true,
      title: 'Your certificate is ready',
      body: 'You\u2019ve completed {course_title}. Open the course to view and download it.',
    },
    course_announcement: {
      enabled: true,
      title: '{course_title}: {announcement_title}',
      body: 'Your instructor posted an announcement. Open the course to read it.',
    },
    learning_reminder: {
      enabled: true,
      title: 'Pick up where you left off',
      body: 'It\u2019s been {days} days since you opened {course_title}. Your next lesson is waiting.',
    },
    subscription_renewed: {
      enabled: true,
      title: 'Your {creator_name} subscription renewed',
      body: '{amount} {currency} was paid from your wallet for {period}. Your access now runs to {ends_at}.',
    },
    subscription_renewal_failed: {
      enabled: true,
      title: 'We couldn\u2019t renew your {creator_name} subscription',
      body: '{reason} {next_step}',
    },
    subscription_expiring: {
      enabled: true,
      title: 'Your {creator_name} subscription ends in {days} days',
      body: 'Your access ends on {ends_at}. Renew from My Courses to keep learning.',
    },
    subscription_card_renewed: {
      enabled: true,
      title: 'Your {creator_name} subscription renewed',
      body: '{amount} {currency} was charged to your card for {period}. Your access now runs to {ends_at}.',
    },
    // ── LMS Phase 2: creator events ──
    creator_approved: {
      enabled: true,
      title: 'Your creator account is approved',
      body: 'You can now publish courses and request payouts.',
    },
    creator_rejected: {
      enabled: true,
      title: 'Your creator application wasn\u2019t approved',
      body: 'Reason: {reason}.',
    },
    creator_suspended: {
      enabled: true,
      title: 'Your creator account has been suspended',
      body: 'Reason: {reason}.',
    },
    course_approved: {
      enabled: true,
      title: '{course_title} is approved',
      body: 'Your course passed review and is listed in the catalog once published.',
    },
    course_rejected: {
      enabled: true,
      title: '{course_title} needs changes',
      body: 'Not approved: {reason}. Update the listing and it goes back into review.',
    },
    subscription_plan_approved: {
      enabled: true,
      title: 'Your {period} subscription is approved',
      body: 'Members can now subscribe to your courses on the {period} plan.',
    },
    subscription_plan_rejected: {
      enabled: true,
      title: 'Your {period} subscription needs changes',
      body: 'Not approved: {reason}. Update the price and it goes back into review.',
    },
    course_content_approved: {
      enabled: true,
      title: '{course_title} — content changes approved',
      body: 'The lessons you changed have been reviewed and cleared.',
    },
    course_content_rejected: {
      enabled: true,
      title: '{course_title} — content changes rejected',
      body: 'Not approved: {reason}. Fix the lessons and they go back into review.',
    },
    creator_sale: {
      enabled: true,
      title: 'You made a sale!',
      body: '{course_title} sold \u2014 {amount} {currency} added to your wallet (order {order_code}).',
    },
    creator_payout_paid: {
      enabled: true,
      title: 'Payout sent',
      body: 'Your payout of {amount} {currency} via {method} has been paid.',
    },
    creator_payout_rejected: {
      enabled: true,
      title: 'Payout request rejected',
      body: 'Your payout request of {amount} {currency} was rejected: {reason}. The funds are back in your wallet.',
    },
    creator_question: {
      enabled: true,
      title: 'New question in {course_title}',
      body: '{learner_name} asked a question in {lesson_title}. Reply from your Questions inbox.',
    },
    creator_grading: {
      enabled: true,
      title: 'A test is waiting for your review',
      body: '{learner_name} submitted {test_title} in {course_title}. Their certificate is on hold until you grade it.',
    },
  },
};

/** See `TenantFeatures.manual_payment_admin_email`. */
export type ManualPaymentAdminEmailConfig = {
  enabled: boolean;
  /** Explicit recipient list; empty ⇒ every super_admin user's email. */
  recipients: string[];
};

export const MANUAL_PAYMENT_ADMIN_EMAIL_DEFAULT: ManualPaymentAdminEmailConfig = {
  enabled: false,
  recipients: [],
};

export const TENANT_FEATURE_DEFAULTS: TenantFeatures = {
  kit_codes_transferable: true,
  kit_codes_expire_days: null,
  kit_codes_downline_transfer: false,
  member_2fa_required: false,
  // Reversible by default: a member asking to close their account is not
  // usually asking to be erased, and erasure cannot be undone.
  account_archive_mode: 'deactivate',
  // Off by default: it locks members out of a live tenant.
  inactivity_block_enabled: false,
  inactivity_block_days: 365,
  inactivity_block_since: null,
  ecom_event_on: 'payment_approved',
  kit_code_reveal_on: 'completed',
  order_auto_complete_days: 7,
  // Off by default: unlike auto-complete, this one destroys a live order.
  order_auto_cancel_unpaid_days: null,
  virtual_orders_auto_complete: true,
  // Off by default: splitting a cart changes how many orders a buyer sees for
  // one payment, so no live tenant gets it without asking.
  virtual_orders_split_checkout: false,
  disabled_plans: [],
  earnings_breakdown_order: [],
  earnings_breakdown_rows: [],
  wallet_topup_enabled: false,
  wallet_topup_brackets: [],
  distribution_run_only_plans: [],
  company_auto_maintained_plans: ['stairstep', 'unilevel'],
  daily_caps: {},
  auto_place_slots: false,
  manual_placement_on_redeem: false,
  genealogy_activate_slot: true,
  free_slots_earn: true,
  package_replicated_link_enabled: true,
  total_saved_counter_enabled: false,
  cd_orders_pay_upline: true,
  cd_stairstep_count_immediately: true,
  half_cd_stairstep_count_immediately: true,
  cd_binary_mentor_count_directs: true,
  cd_binary_placement_count_directs: true,
  half_cd_triggers_like_ps: false,
  half_cd_debt_plans: [],
  cd_block_payout_in_debt: true,
  cd_topup_settles_debt: true,
  cd_topup_bonus_treatment: 'principal_only',
  max_slots_per_member: null,
  slot_code_series: { enabled: false },
  paid_upgrade_refires_bonuses: false,
  stairstep_live_rankup: true,
  stairstep_rankup_bonus: false,
  rank_showcase_enabled: false,
  payout_deductions: PAYOUT_DEDUCTIONS_DEFAULT,
  cashout_schedule: CASHOUT_SCHEDULE_DEFAULT,
  cashout_require_kyc: true,
  lock_cashout_name_to_owner: false,
  cashout_contact_autofill: 'off',
  payout_qr_required: false,
  unique_cashout_account_per_user: false,
  cod_requires_kyc: false,
  manual_payment_force_approve: false,
  kyc_requirements: KYC_REQUIREMENTS_DEFAULT,
  cashout_limits: CASHOUT_LIMITS_DEFAULT,
  multiple_cashout_requests_allowed: true,
  cashout_multiple_scope: 'member',
  cashout_cancellation_allowed: true,
  payout_export: PAYOUT_EXPORT_DEFAULT,
  ecom_payment_methods: ECOM_PAYMENT_METHODS_DEFAULT,
  pos_payment_methods: POS_PAYMENT_METHODS_DEFAULT,
  pos_custom_fees: POS_CUSTOM_FEES_DEFAULT,
  kit_auto_create_slot: { ecom: false, cashier: false, virtual: false },
  gateway_payments_enabled: false,
  terms_text: '',
  terms_version: 1,
  privacy_policy_text: '',
  privacy_policy_version: 1,
  require_terms_on_signup: false,
  require_username_on_signup: false,
  registration_fields: [],
  member_profile_editable: MEMBER_EDITABLE_FIELDS_DEFAULT,
  merchant_profile_editable: MERCHANT_EDITABLE_FIELDS_DEFAULT,
  allow_login_by_username: false,
  activation_terms_text: '',
  activation_terms_version: 1,
  activation_success_message: 'Congratulations! Your slot is now active.',
  require_activation_terms: false,
  order_success_message: "Thank you for your order! We've received it and will process it shortly.",
  unpaid_orders_dialog_message:
    'You have unpaid order/s, would you like to proceed with the payment steps?',
  default_sponsor_user_id: null,
  default_sponsor_slot_code: null,
  link_sponsor_override_slot_code: null,
  extra_slots_self_sponsor: true,
  extra_slots_use_registration_slot: true,
  sponsor_editable_from_replicated_link: true,
  sponsor_editable_extra_slots: true,
  sponsor_editable_self_stack: true,
  redeem_for_downline_direct_only: true,
  show_sponsor_on_dashboard: false,
  binary_genealogy_node_fields: BINARY_GENEALOGY_NODE_FIELDS_DEFAULT,
  domain_expires_at: null,
  show_sponsor_on_redeem: true,
  signup_mode: 'on',
  public_site_info_only: false,
  product_feed_hide_featured_packages: false,
  product_feed_hide_featured_products: false,
  campaign_page_signup_allowed: false,
  member_referral_code_self_set: false,
  referral_code_max_changes: 1,
  gpv_includes_ppv: true,
  product_code_generation: false,
  stairstep_exclude_personal_pv: false,
  member_pv_display: 'ppv_and_gpv',
  dropshipping_income: false,
  store_wide_dropship_link: false,
  virtual_funnel_dropship_attribution: false,
  replicated_link_pv_to_owner: false,
  replicated_link_ref_encrypted: false,
  replicated_page_enabled: false,
  virtual_products_open_replicated_page: false,
  // Off by default: an existing tenant's admin is unchanged by the upgrade, and
  // a tenant that sells only physical goods never sees the concept at all.
  virtual_products_enabled: false,
  // Off by default: an upgrade must not add a Materials page or a Recipe tab to
  // a tenant that only resells finished goods.
  materials_enabled: false,
  // Off by default: a behaviour switch. Until a tenant opts in, no stock path
  // writes or reads a lot.
  inventory_lots_enabled: false,
  lot_pick_order: 'fifo',
  item_locator_enabled: false,
  // Off by default: a behaviour switch, and an upgrade must not add pages or till
  // buttons to a tenant that never set a gift check up.
  vouchers_enabled: false,
  voucher_label: 'Gift Check',
  birthday_claim_window_days: 0,
  birthday_requires_kyc: true,
  // Hidden by default: an upgrade must not add a field to anyone's signup form.
  birth_date_field: 'hidden',
  signup_free_slot: SIGNUP_FREE_SLOT_DEFAULT,
  pos_topup_enabled: false,
  pos_topup_exact_methods_enabled: false,
  pos_topup_pin_above: null,
  // Off by default: an upgrade must not add a nav item to a tenant that has
  // published no resources.
  member_resources_enabled: false,
  member_resources_office_viewer: false,
  lms_enabled: false,
  member_dashboard_mode: 'mlm',
  show_team_earnings: true,
  course_video_max_mb: 1024,
  storage_sweep_enabled: true,
  course_embed_hosts: [...DEFAULT_EMBED_HOSTS],
  storage_delete_grace_days: 7,
  lms_nudge_after_days: 7,
  pos_course_sale_mode: 'warn',
  lms_content_rereview: 'hold',
  lms_content_rereview_scope: 'all',
  lms_abuse_reporting_enabled: true,
  lms_seek_restriction: false,
  lms_manual_completion: true,
  lms_public_catalog: true,
  lms_app_web_link: false,
  lms_subscription_model: 'tenant',
  lms_subscription_eligibility: 'activated_slot',
  lms_subscription_auto_renew_enabled: true,
  lms_subscription_card_recurring: false,
  commissions_on_non_cash: false,
  wallet_orders_charge_shipping: true,
  wallet_orders_auto_complete: false,
  virtual_instant_pay: false,
  unpaid_order_lock_enabled: false,
  member_order_cancel_enabled: false,
  virtual_checkout_resume_on_login: false,
  accounting_enabled: false,
  consignment_enabled: false,
  stockist_consignment_enabled: false,
  funds_account_required: false,
  house_vat_enabled: false,
  house_vat_default_pct: '12',
  accounting_posting_start_date: null,
  accounting_fiscal_year_start_month: 1,
  ar_invoicing_enabled: false,
  ar_default_terms_code: 'due_on_receipt',
  ar_credit_limit_mode: 'warn',
  platform_edition: 'mlm',
  non_activated_pricing_membership_id: null,
  // Default preserves today's behaviour: price follows the SELECTED slot.
  pricing_slot_source: 'active_slot',
  coin_label: 'Coins',
  coin_rewards: {},
  ai_credit_rewards: {},
  ledger_labels: {},
  ledger_source_plan_labels: {},
  notification_templates: NOTIFICATION_TEMPLATES_DEFAULT,
  manual_payment_admin_email: MANUAL_PAYMENT_ADMIN_EMAIL_DEFAULT,
  platform_fee_pct: '10',
  platform_fee_min_pct: '0',
  platform_fee_max_pct: '100',
  merchant_pv_commission_pct: '50',
  merchant_default_vat_pct: '12',
  merchant_rtu_ceiling_pct: '20',
  merchant_rtu_default_shares: ['50', '30', '10', '5', '5'],
  merchant_vat_enabled_default: false,
  merchant_sales_earn_comp: false,
  order_by_area: false,
  stockist_receivables: false,
  merchant_requirements: MERCHANT_REQUIREMENTS_DEFAULT,
  merchant_payout_deductions: PAYOUT_DEDUCTIONS_DEFAULT,
  merchant_payout_schedule: 'monthly',
  creator_platform_fee_pct: '30',
  creator_platform_fee_min_pct: '0',
  creator_platform_fee_max_pct: '100',
  creator_sales_earn_comp: false,
  creator_requirements: [],
  creator_payout_deductions: PAYOUT_DEDUCTIONS_DEFAULT,
  flash_sale: { enabled: false, productIds: [], endsAt: null },
  landing_packages: { productIds: [] },
  featured_packages: { enabled: false, productIds: [], heading: null },
  storefront: STOREFRONT_DEFAULT,
  messaging_enabled: false,
  chat_spam_autoblock_enabled: false,
  chat_spam_window_seconds: 300,
  chat_spam_threshold: 60,
  autoship_enabled: true,
  member_subscription_enabled: false,
  member_subscription: { price: '0', currency: 'cash' },
  assistant_enabled: false,
  assistant: ASSISTANT_CONFIG_DEFAULT,
  coaching_enabled: false,
  coaching: COACHING_CONFIG_DEFAULT,
};

/**
 * R2 — "edition = data". The flag set that turns a stock deployment into the
 * books-only Accounting Edition, applied by a tenant preset's `features`
 * overlay (see packages/db/src/tenant-presets.ts). There is no fork and no
 * build flag: an edition is just a starting point in the same feature space,
 * so a tenant can still turn any individual capability back on afterwards.
 *
 * It turns the books ON and the network OFF. Note it deliberately does NOT set
 * `accounting_posting_start_date` — that is pinned per-box at enable time by
 * the runbook, and a preset guessing it would strand history.
 */
export const EDITION_FEATURE_OVERRIDES: Record<string, Partial<TenantFeatures>> = {
  accounting: {
    platform_edition: 'accounting',
    accounting_enabled: true,
    ar_invoicing_enabled: true,
    consignment_enabled: true,
    house_vat_enabled: true,
    funds_account_required: true,
    // A books tenant has no public network funnel.
    signup_mode: 'by_sponsor_only',
    public_site_info_only: true,
    replicated_page_enabled: false,
    lms_enabled: false,
    wallet_topup_enabled: false,
    gateway_payments_enabled: false,
    virtual_products_enabled: false,
    member_resources_enabled: false,
    campaign_page_signup_allowed: false,
    package_replicated_link_enabled: false,
  },
};

/**
 * Merge a partial features blob (from DB jsonb) with defaults — guarantees
 * every key is present and correctly typed. Unknown keys in `raw` are
 * preserved (forward compat: older code reading a newer DB doesn't drop
 * data).
 */
export function resolveFeatures(raw: Record<string, unknown> | null | undefined): TenantFeatures {
  const out = { ...TENANT_FEATURE_DEFAULTS } as TenantFeatures & Record<string, unknown>;
  if (!raw) return out;
  for (const key of Object.keys(raw)) {
    const v = raw[key];
    if (v === undefined) continue;
    (out as Record<string, unknown>)[key] = v;
  }
  // `storefront` is a nested config; deep-merge it against defaults so a blob
  // missing newer sub-blocks (forward-compat) still resolves to a complete,
  // correctly-typed config rather than dropping keys the shallow loop above
  // would leave partial.
  out.storefront = resolveStorefront(out.storefront as Partial<StorefrontConfig>);
  // Deep-merge notification templates so a blob missing newer event codes still
  // resolves every event to a default (forward-compat), and an admin who edited
  // one event keeps the rest defaulted.
  const nt = out.notification_templates as Partial<NotificationTemplatesConfig> | undefined;
  // Per-EVENT and per-FIELD. A stored event object used to REPLACE its default
  // wholesale, so a blob carrying `{enabled: false}` alone (a hand-edited
  // jsonb, or any partial write) resolved to a template with no `title`/`body`
  // and `renderNotificationTemplate` threw on `undefined.replace` — taking down
  // whatever write the notification was firing inside of. Merging each event
  // over its default makes a partial impossible to crash on; an unknown code
  // (one we no longer ship) still passes through untouched.
  const mergedEvents: Record<string, NotificationEventTemplate> = {
    ...NOTIFICATION_TEMPLATES_DEFAULT.events,
  };
  for (const [code, tpl] of Object.entries(nt?.events ?? {})) {
    const base = NOTIFICATION_TEMPLATES_DEFAULT.events?.[code];
    mergedEvents[code] = base
      ? { ...base, ...(tpl as Partial<NotificationEventTemplate>) }
      : (tpl as NotificationEventTemplate);
  }
  out.notification_templates = {
    earnings: { ...NOTIFICATION_TEMPLATES_DEFAULT.earnings, ...(nt?.earnings ?? {}) },
    events: mergedEvents,
  };
  // Deep-merge cashout_limits so a blob missing a newer bound key still resolves
  // every bound (forward-compat), defaulting absent bounds to null (unbounded).
  out.cashout_limits = {
    ...CASHOUT_LIMITS_DEFAULT,
    ...(out.cashout_limits as Partial<CashoutLimits> | undefined),
  };
  // Same forward-compat deep-merge: a blob saved as `{ enabled: true }` alone
  // must still resolve `recipients` to [].
  out.manual_payment_admin_email = {
    ...MANUAL_PAYMENT_ADMIN_EMAIL_DEFAULT,
    ...(out.manual_payment_admin_email as Partial<ManualPaymentAdminEmailConfig> | undefined),
  };
  // Same forward-compat reason: a tenant saved before a column existed must
  // still resolve every key, else the export emits `undefined` into a cell.
  out.payout_export = {
    ...PAYOUT_EXPORT_DEFAULT,
    ...(out.payout_export as Partial<PayoutExportConfig> | undefined),
  };
  out.signup_free_slot = {
    ...SIGNUP_FREE_SLOT_DEFAULT,
    ...(out.signup_free_slot as Partial<SignupFreeSlotConfig> | undefined),
  };
  // Deep-merge editable-field configs so a blob missing a newer field key still
  // resolves every flag (forward-compat), defaulting absent flags to editable.
  out.member_profile_editable = {
    ...MEMBER_EDITABLE_FIELDS_DEFAULT,
    ...(out.member_profile_editable as Partial<MemberEditableFields> | undefined),
  };
  out.binary_genealogy_node_fields = {
    ...BINARY_GENEALOGY_NODE_FIELDS_DEFAULT,
    ...(out.binary_genealogy_node_fields as Partial<BinaryGenealogyNodeFields> | undefined),
  };
  out.merchant_profile_editable = {
    ...MERCHANT_EDITABLE_FIELDS_DEFAULT,
    ...(out.merchant_profile_editable as Partial<MerchantEditableFields> | undefined),
  };
  // F5: deep-merge the per-channel auto-create flag so a blob with only one
  // channel set still resolves the other to its default (forward-compat).
  out.kit_auto_create_slot = {
    ecom: false,
    cashier: false,
    virtual: false,
    ...(out.kit_auto_create_slot as
      | Partial<{ ecom: boolean; cashier: boolean; virtual: boolean }>
      | undefined),
  };
  // Deep-merge the subscription plan config so a blob with only `price` set still
  // resolves `currency` to its default (forward-compat).
  out.member_subscription = {
    price: '0',
    currency: 'cash',
    ...(out.member_subscription as Partial<{ price: string; currency: 'cash' | 'gc' }> | undefined),
  };
  // Deep-merge the AI Assistant config so a blob missing newer keys still
  // resolves complete (forward-compat), and clamp an unknown/stale model id
  // back to the default so the API never sends an unallowlisted model.
  out.assistant = {
    ...ASSISTANT_CONFIG_DEFAULT,
    ...(out.assistant as Partial<AssistantConfig> | undefined),
  };
  if (!(ASSISTANT_MODELS as readonly string[]).includes(out.assistant.model)) {
    out.assistant.model = ASSISTANT_CONFIG_DEFAULT.model;
  }
  // Same forward-compat deep-merge for coaching, with the same clamps: an
  // unknown model or provider id falls back to the default rather than reaching
  // an upstream API.
  out.coaching = {
    ...COACHING_CONFIG_DEFAULT,
    ...(out.coaching as Partial<CoachingConfig> | undefined),
  };
  if (!(ASSISTANT_MODELS as readonly string[]).includes(out.coaching.model)) {
    out.coaching.model = COACHING_CONFIG_DEFAULT.model;
  }
  if (!(COACHING_ASTRO_PROVIDERS as readonly string[]).includes(out.coaching.astro_provider)) {
    out.coaching.astro_provider = COACHING_CONFIG_DEFAULT.astro_provider;
  }
  return out;
}
